import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  ArrowLeft, ChevronLeft, ChevronRight, ChevronDown,
  CheckCircle, Clock, BookOpen, Layers, Home,
  AlertCircle, Lightbulb, ArrowUpRight, Lock,
  PenLine, FileText, Mail, MessageSquare, Edit3, ListChecks,
  Sparkles, Send, RotateCcw, ChevronUp,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useLocalizedNavigate } from '../hooks/useLocalizedNavigate';
import GrammarBottomNav from '../components/grammar/GrammarBottomNav';
import PracticeHistoryPanel from '../components/practice/PracticeHistoryPanel';
import SampleSelectView from '../components/practice/SampleSelectView';
import adminService from '../services/adminService';
import subscriptionService from '../services/subscriptionService';
import { auth, db } from '../firebase';
import { collection, addDoc, onSnapshot, doc, serverTimestamp, query, where, orderBy, limit, getDocs } from 'firebase/firestore';
import enhancedUserService from '../services/enhancedUserService';
import { PracticeWritingFeedback } from '../components/practice/writing/WritingFeedbackPanel.jsx';
import AskTeacherPanel from '../components/practice/writing/AskTeacherPanel.jsx';
import { readSession, writeSession } from '../utils/sessionStore';
import freeAttemptService from '../services/freeAttemptService';
import { marked } from 'marked';
import LogamiLogoSpinner from '../components/ui/LogamiLogoSpinner';
import AhaUpsellModal from '../components/ui/AhaUpsellModal';
import { trackPaywallShown } from '../utils/conversionEvents';

function useStopwatch() {
  const [elapsed, setElapsed] = useState(0);
  const [running, setRunning] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (running) ref.current = setInterval(() => setElapsed((e) => e + 1), 1000);
    else clearInterval(ref.current);
    return () => clearInterval(ref.current);
  }, [running]);

  const toggle = () => setRunning((r) => !r);
  const reset = () => {
    setRunning(false);
    setElapsed(0);
  };
  const fmt = (s) => `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;

  return { elapsed, running, toggle, reset, fmt };
}

const PART_TYPE_LABELS = {
  'academic-forum-post': 'Academic Forum Post',
  'academic-position-paper': 'Academic Position Paper',
  'business-complaint': 'Business Complaint',
  'business-proposal': 'Business Proposal',
  'choice-writing': 'Choice Writing',
  'choice-writing-essay': 'Choice Writing (Essay)',
  'email-opinion': 'Opinion E-Mail',
  'email-response-community': 'Community E-Mail Response',
  'email-response-professional': 'Professional E-Mail Response',
  email: 'Informelle E-Mail',
  'email-formal': 'Formelle E-Mail',
  'formal-complaint': 'Formal Complaint',
  'formal-letter': 'Formal Letter',
  'formal-message': 'Formal Message',
  'formal-response-email': 'Formal E-Mail Response',
  'forum-post': 'Diskussionsbeitrag / Forum',
  'internal-report': 'Internal Report',
  'internal-summary-recommendation': 'Summary and Recommendation',
  'letter-to-the-editor': 'Letter to the Editor',
  essay: 'Aufsatz / Essay',
  letter: 'Brief',
  report: 'Bericht',
  comment: 'Kommentar',
  description: 'Beschreibung',
};

const PART_ICON_COMPONENTS = {
  'academic-forum-post': MessageSquare,
  'academic-position-paper': FileText,
  'business-complaint': FileText,
  'business-proposal': FileText,
  'choice-writing': Edit3,
  'choice-writing-essay': Edit3,
  'email-opinion': Mail,
  'email-response-community': Mail,
  'email-response-professional': Mail,
  email: Mail,
  'email-formal': FileText,
  'formal-complaint': FileText,
  'formal-letter': FileText,
  'formal-message': FileText,
  'formal-response-email': FileText,
  'forum-post': MessageSquare,
  'internal-report': FileText,
  'internal-summary-recommendation': FileText,
  'letter-to-the-editor': MessageSquare,
  essay: Edit3,
  letter: Edit3,
  report: Edit3,
  comment: Edit3,
  description: Edit3,
};

const ELEVATED_SURFACE =
  'relative overflow-hidden rounded-2xl border-2 border-black/10 bg-white shadow-[0_10px_28px_rgba(15,23,42,0.06)]';
const SOFT_SURFACE =
  'rounded-2xl border border-black/10 bg-white/95 backdrop-blur-sm shadow-[0_4px_14px_rgba(15,23,42,0.05)]';

const FORMAL_TASK_TYPES = new Set([
  'business-complaint',
  'business-proposal',
  'email-formal',
  'email-response-professional',
  'formal-complaint',
  'formal-letter',
  'formal-message',
  'formal-response-email',
  'internal-report',
  'internal-summary-recommendation',
]);

const EMAIL_TASK_TYPES = new Set([
  'business-complaint',
  'email',
  'email-formal',
  'email-opinion',
  'email-response-community',
  'email-response-professional',
  'formal-complaint',
  'formal-letter',
  'formal-message',
  'formal-response-email',
  'letter',
]);

const DISCUSSION_TASK_TYPES = new Set([
  'academic-forum-post',
  'academic-position-paper',
  'choice-writing-essay',
  'comment',
  'description',
  'essay',
  'forum-post',
  'letter-to-the-editor',
]);

const REPORT_TASK_TYPES = new Set([
  'business-proposal',
  'internal-report',
  'internal-summary-recommendation',
  'report',
]);

const FORUM_INPUT_TYPES = new Set(['forum-post']);

const MATCH_STOPWORDS = new Set([
  'aber',
  'alle',
  'auch',
  'beim',
  'dann',
  'dass',
  'dem',
  'den',
  'der',
  'des',
  'die',
  'eine',
  'einer',
  'eines',
  'einen',
  'fuer',
  'ist',
  'mit',
  'nach',
  'oder',
  'sich',
  'sind',
  'und',
  'von',
  'wird',
]);

const FIELD_REDEMITTEL_CONTEXT_ALIASES = {
  subject: ['betreff', 'betreffzeile', 'thema', 'anliegen'],
  salutation: ['anrede', 'formelle anrede', 'informelle anrede', 'sehr formelle e-mail/brief'],
  introduction: ['einleitung', 'grund des schreibens', 'einleitung: bezugnahme', 'bezugnahme'],
  mainBody: [
    'hauptteil',
    'argument',
    'begruendung',
    'problem',
    'loesungsvorschlag',
    'beispiel',
    'frage',
    'pro und contra',
    'these',
    'eroerterung',
  ],
  context: ['bezugnahme', 'bezug', 'hintergrund', 'kontext'],
  analysis: ['analyse', 'grafenbeschreibung', 'grafik beschreiben', 'these', 'abwaegung'],
  recommendation: ['vorschlag', 'empfehlung', 'appell', 'ausblick', 'loesungsvorschlag'],
  reference: ['bezugnahme', 'bezug', 'einleitung – referenz', 'einleitung - referenz', 'quelle'],
  request: ['bitte', 'bitte aeussern', 'hoefliche bitte', 'information anfordern', 'forderung'],
  appeal: ['appell', 'wunsch', 'schluss – ausblick', 'schluss - ausblick'],
  closing: ['schluss', 'schlussphrase', 'schluss und gruss', 'grussformel', 'abschluss'],
  signature: ['unterschrift', 'signatur', 'unterzeichnung', 'gruss und name', 'schluss und gruss'],
  title: ['thema', 'titel', 'these', 'position'],
  senderAddress: ['formeller brief', 'formelle e-mail', 'anschrift', 'briefanrede'],
  recipientAddress: ['formeller brief', 'formelle e-mail', 'anschrift', 'briefanrede'],
  dateLine: ['formeller brief', 'formelle e-mail', 'anschrift'],
};

const TASK_REDEMITTEL_CONTEXT_ALIASES = {
  email: ['informelle e-mail', 'einleitung', 'hauptteil', 'vorschlag', 'schluss'],
  'email-formal': ['formelle e-mail', 'formelle anrede', 'hoefliche bitte', 'schluss'],
  'formal-message': ['formelle einleitung', 'hoefliche bitte', 'problem schildern', 'schluss'],
  'formal-letter': ['formeller brief', 'briefanrede', 'beschwerde', 'forderung', 'grussformel'],
  'formal-response-email': ['bezugnahme', 'formelle einleitung', 'bitte', 'schlussformel'],
  'formal-complaint': ['beschwerde', 'problem schildern', 'forderung', 'loesungsvorschlag', 'grussformel'],
  'business-complaint': ['beschwerde', 'problem schildern', 'forderung', 'loesungsvorschlag'],
  'business-proposal': ['vorschlag', 'analyse', 'empfehlung', 'strukturierung'],
  'forum-post': ['forumsbeitrag', 'meinung', 'begruendung', 'argument', 'abschluss'],
  'academic-forum-post': ['forum', 'these', 'begruendung', 'abwaegung', 'schluss'],
  'academic-position-paper': ['stellungnahme', 'these', 'argument', 'begruendung', 'schluss'],
  'choice-writing-essay': ['essay', 'stellungnahme', 'argumentation', 'eroerterung', 'schluss'],
  'letter-to-the-editor': ['leserbrief', 'meinung', 'argument', 'appell', 'schluss'],
  'internal-report': ['bericht', 'analyse', 'zusammenfassung', 'empfehlung'],
  'internal-summary-recommendation': ['zusammenfassung', 'analyse', 'empfehlung', 'ausblick'],
  'email-opinion': ['stellungnahme', 'meinung', 'begruendung', 'schluss'],
  'email-response-community': ['bezugnahme', 'meinung', 'vorschlag', 'schluss'],
  'email-response-professional': ['bezugnahme', 'formelle einleitung', 'vorschlag', 'schluss'],
};

const PROFILE_REDEMITTEL_CONTEXT_ALIASES = {
  'goethe-b1': ['informelle e-mail', 'forumsbeitrag', 'formelle nachricht'],
  'goethe-b2': ['forumsbeitrag', 'formelle nachricht', 'argument'],
  'goethe-c1': ['argumentation', 'grafik', 'formell'],
  'osd-b1': ['formelle e-mail', 'informelle e-mail', 'hauptteil'],
  'osd-b2': ['formeller brief', 'argumentativer beitrag', 'meinung'],
  'osd-c1': ['formelle e-mail', 'stellungnahme', 'kommentar', 'strukturierung'],
  'telc-b1': ['formelle e-mail', 'informelle e-mail', 'betreffzeile', 'schluss und gruss'],
  'telc-b2': ['formeller brief', 'information anfordern', 'beschwerde'],
  'telc-c1': ['stellungnahme', 'essay', 'abwaegung', 'ausblick'],
};

const FIELD_ROLE_TARGETS = {
  appeal: ['appeal', 'recommendation', 'closing'],
  analysis: ['analysis', 'mainBody', 'reference'],
  closing: ['closing'],
  contentpoint: ['mainBody', 'analysis', 'request', 'recommendation'],
  context: ['introduction', 'reference', 'analysis'],
  dateline: ['dateLine'],
  introduction: ['introduction', 'reference'],
  mainbody: ['mainBody', 'analysis', 'request', 'recommendation'],
  name: ['signature'],
  recommendation: ['recommendation', 'appeal', 'request'],
  recipientaddress: ['address'],
  reference: ['reference', 'introduction'],
  request: ['request', 'recommendation'],
  salutation: ['salutation'],
  senderaddress: ['address'],
  signature: ['signature', 'closing'],
  subject: ['subject'],
  title: ['title', 'introduction'],
};

const PROFILE_TASK_SECTION_ALLOWLIST = {
  'goethe-b1': {
    email: ['aufgabe1_informell'],
    'email-formal': ['aufgabe3_formell'],
    'forum-post': ['aufgabe2_forum'],
  },
  'goethe-b2': {
    'formal-message': ['teil2_formell'],
    'forum-post': ['teil1_forum'],
  },
  'goethe-c1': {
    'choice-writing-essay': ['teil1_aufsatz'],
    'formal-complaint': ['teil3_pruefungsnahe_aufgaben'],
    'forum-post': ['teil3_pruefungsnahe_aufgaben'],
  },
  'osd-b1': {
    email: {
      formal: ['formelle_email'],
      informal: ['informelle_email'],
      neutral: ['informelle_email', 'formelle_email'],
    },
  },
  'osd-b2': {
    'choice-writing-essay': ['aufgabe2_argument'],
    'formal-letter': ['aufgabe1_brief'],
  },
  'osd-c1': {
    'choice-writing-essay': ['aufgabe2_referat'],
    'formal-response-email': ['aufgabe1_formell'],
  },
  'telc-b1': {
    email: {
      formal: ['aufgabe_email_brief'],
      informal: ['informelle_email'],
      neutral: ['informelle_email', 'aufgabe_email_brief'],
    },
  },
  'telc-b2': {
    'formal-letter': ['aufgabe_formell'],
  },
  'telc-c1': {
    'academic-forum-post': ['aufgabe_email_und_wahlaufgabe', 'aufgabe_essay'],
    'academic-position-paper': ['aufgabe_email_und_wahlaufgabe', 'aufgabe_essay'],
    'business-complaint': ['aufgabe_email_und_wahlaufgabe'],
    'business-proposal': ['aufgabe_email_und_wahlaufgabe'],
    'choice-writing': ['aufgabe_essay', 'aufgabe_email_und_wahlaufgabe'],
    'choice-writing-essay': ['aufgabe_essay', 'aufgabe_email_und_wahlaufgabe'],
    'email-opinion': ['aufgabe_email_und_wahlaufgabe'],
    'email-response-community': ['aufgabe_email_und_wahlaufgabe'],
    'email-response-professional': ['aufgabe_email_und_wahlaufgabe'],
    'formal-letter': ['aufgabe_email_und_wahlaufgabe'],
    'forum-post': ['aufgabe_email_und_wahlaufgabe', 'aufgabe_essay'],
    'internal-report': ['aufgabe_email_und_wahlaufgabe'],
    'internal-summary-recommendation': ['aufgabe_email_und_wahlaufgabe'],
    'letter-to-the-editor': ['aufgabe_email_und_wahlaufgabe', 'aufgabe_essay'],
  },
};

function normalizeFieldKey(fieldId = '') {
  const normalized = normalizeForMatch(fieldId);
  if (normalized.startsWith('contentpoint')) return 'contentpoint';
  return normalized;
}

function getFieldRoleTargets(fieldId = '') {
  const key = normalizeFieldKey(fieldId);
  return FIELD_ROLE_TARGETS[key] || FIELD_ROLE_TARGETS.mainbody;
}

function inferTaskRegister(activeTask, taskType, examProfile = '') {
  const normalizedType = String(taskType || '').toLowerCase();
  const rawSignal = normalizeForMatch(
    `${normalizedType} ${activeTask?.taskType || ''} ${activeTask?.scenario || ''} ${activeTask?.inputContent || ''}`
  );

  if (
    FORMAL_TASK_TYPES.has(normalizedType) ||
    /formal|beschwerde|vorgesetz|firma|institut|seminarassist|sehr geehrte|damen und herren|abteilungsleiter/.test(rawSignal)
  ) {
    return 'formal';
  }

  if (/liebe|hallo|freund|freundin|kolleg|nachbar|dein|deine|herzliche grusse|viele grusse/.test(rawSignal)) {
    return 'informal';
  }

  if (/forum|aufsatz|essay|stellungnahme|kommentar|bericht|diskussion|referat/.test(rawSignal)) {
    return 'neutral';
  }

  if (examProfile === 'telc-b1') return 'informal';
  return 'neutral';
}

function inferCardRegister(card) {
  const signal = normalizeForMatch(`${card?.context || ''} ${card?.sectionName || ''} ${card?.front || ''}`);

  if (/informell|liebe|lieber|hallo|dein|deine|schreib bald|ich melde mich bald/.test(signal)) {
    return 'informal';
  }

  if (
    /formell|sehr geehrte|sehr geehrter|damen und herren|hochachtungsvoll|mit freundlichen|beschwerde|forderung|anrede/.test(signal)
  ) {
    return 'formal';
  }

  return 'neutral';
}

function inferCardRoles(card) {
  const context = normalizeForMatch(card?.context || '');
  const front = normalizeForMatch(card?.front || '');
  const section = normalizeForMatch(card?.sectionName || '');
  const signal = `${context} ${section} ${front}`;
  const roles = new Set();

  if (/umformung|nominal|partizipial|passiv|konjunktiv|sequenzierung|varierte verben|variierte verben/.test(signal)) {
    roles.add('exclude');
    return Array.from(roles);
  }

  if (/absender|anschrift|empfaenger|briefkopf/.test(signal)) roles.add('address');
  if (/datum|ort und datum/.test(signal)) roles.add('dateLine');
  if (/betreff/.test(signal) || front.startsWith('betreff')) roles.add('subject');

  if (/anrede|sehr geehrte|sehr geehrter|damen und herren|liebe|lieber|hallo|guten tag/.test(signal)) {
    roles.add('salutation');
  }

  if (
    /schlussphrase|schlussformel|grussformel|grußformel|schluss und gruss|schluss: bitte um antwort|formeller schluss|schluss/.test(signal) ||
    /mit freundlichen|hochachtungsvoll|viele grusse|liebe grusse|ich freue mich auf ihre ruckmeldung/.test(front)
  ) {
    roles.add('closing');
  }

  if (
    /unterschrift|signatur|unterzeichnung|gruss und name/.test(signal) ||
    /dein e|deine|dein|ihr vorname|vorname/.test(front)
  ) {
    roles.add('signature');
  }

  if (/bezug|bezugnahme|referenz|artikel|beitrag|quelle|textwiedergabe/.test(signal)) {
    roles.add('reference');
  }

  if (/einleitung|anlass|grund des schreibens|absicht|thema einfuhren|komplexe analyse einleiten/.test(signal)) {
    roles.add('introduction');
  }

  if (/bitte|frage|information anfordern|forderung|um informationen bitten/.test(signal)) {
    roles.add('request');
  }

  if (/analyse|grafik|grafen|schaubild|zusammenfassung|hauptthese|konjunktiv|sequenzierung|beobachtung/.test(signal)) {
    roles.add('analysis');
  }

  if (/vorschlag|empfehlung|ausblick|kompromiss|losung|lösung/.test(signal)) {
    roles.add('recommendation');
  }

  if (/appell|aufruf|wunsch/.test(signal)) roles.add('appeal');
  if (/titel|thema/.test(signal)) roles.add('title');

  if (
    /meinung|argument|begrundung|begruendung|beispiel|pro und contra|these|abwaegung|gegenargument|problem schildern|kriterien/.test(signal)
  ) {
    roles.add('mainBody');
  }

  if (roles.size === 0) roles.add('mainBody');
  return Array.from(roles);
}

function getAllowedSectionIds(examProfile, taskType, taskRegister) {
  const profileRules = PROFILE_TASK_SECTION_ALLOWLIST[examProfile];
  if (!profileRules) return null;

  const directRule = profileRules[taskType];
  if (Array.isArray(directRule)) return directRule;

  if (directRule && typeof directRule === 'object') {
    return directRule[taskRegister] || directRule.neutral || null;
  }

  return null;
}

function isRegisterCompatible(taskRegister, cardRegister, roles, fieldKey) {
  if (cardRegister === 'neutral' || taskRegister === 'neutral') return true;

  const toneSensitiveField = ['salutation', 'closing', 'signature', 'subject', 'introduction', 'reference'].includes(fieldKey);
  const toneSensitiveRole = roles.some((role) => ['salutation', 'closing', 'signature', 'subject'].includes(role));

  if (!toneSensitiveField && !toneSensitiveRole) return true;
  return taskRegister === cardRegister;
}

function buildGeneratedRedemittelCards({ examProfile, taskType, field, taskRegister }) {
  const fieldKey = normalizeFieldKey(field?.id || '');
  const level = String(examProfile || '').split('-')[1] || 'b2';
  const normalizedTaskType = String(taskType || '').toLowerCase();
  
  // Map field keys to contextual descriptions
  const fieldContextMap = {
    subject: 'Email subject line for clear topic communication',
    salutation: 'Professional or personal opening greeting',
    title: 'Title or heading for discussion or essay',
    reference: 'Reference to previous communication or context',
    introduction: 'Opening statement introducing the topic and purpose',
    context: 'Background information and current situation overview',
    analysis: 'Analytical observation with supporting reasoning',
    request: 'Polite request for action or information',
    recommendation: 'Suggested solution or proposed next steps',
    appeal: 'Persuasive statement or appeal to action',
    closing: 'Conclusion or closing statement summarizing main points',
    signature: 'Sign-off with sender identification',
    senderaddress: 'Sender contact and address information',
    recipientaddress: 'Recipient or destination address block',
    dateline: 'Date and location stamp',
    mainbody: 'Main argument, content point, or elaborate discussion',
    contentpoint: 'Specific content point or expanded argument',
  };
  
  const generatedContext = fieldContextMap[fieldKey] || 'Writing task component for exam preparation';

  const byLevel = (map) => map[level] || map.b2 || map.b1 || [];

  const discussionTask =
    DISCUSSION_TASK_TYPES.has(normalizedTaskType) ||
    normalizedTaskType.includes('forum') ||
    normalizedTaskType.includes('essay') ||
    normalizedTaskType.includes('position-paper');

  const reportTask =
    REPORT_TASK_TYPES.has(normalizedTaskType) ||
    normalizedTaskType.includes('report') ||
    normalizedTaskType.includes('summary');

  const complaintTask =
    normalizedTaskType.includes('complaint') ||
    normalizedTaskType.includes('beschwerde') ||
    normalizedTaskType.includes('formal-letter');

  let fronts = [];

  switch (fieldKey) {
    case 'subject':
      if (taskRegister === 'informal') {
        fronts = byLevel({
          b1: ['Betreff: Unser Treffen am [Datum]', 'Betreff: Danke fuer deine Nachricht'],
          b2: ['Betreff: Kurze Rueckmeldung zu [Thema]', 'Betreff: Plan fuer unser naechstes Treffen'],
          c1: ['Betreff: Rueckmeldung zu deinem Vorschlag', 'Betreff: Abstimmung zum weiteren Vorgehen'],
        });
      } else {
        fronts = byLevel({
          b1: ['Betreff: Anfrage zu [Thema]', 'Betreff: Bitte um Informationen zu [Thema]'],
          b2: ['Betreff: Rueckfrage zu [Thema]', 'Betreff: Beschwerde ueber [Thema]'],
          c1: ['Betreff: Rueckmeldung zu Ihrem Vorschlag betreffend [Thema]', 'Betreff: Bitte um zeitnahe Klaerung von [Thema]'],
        });
      }
      break;
    case 'salutation':
      fronts = taskRegister === 'informal'
        ? ['Liebe/r [Vorname],', 'Hallo [Vorname],']
        : ['Sehr geehrte Frau [Nachname],', 'Sehr geehrter Herr [Nachname],', 'Sehr geehrte Damen und Herren,'];
      break;
    case 'title':
      fronts = byLevel({
        b1: ['Meine Meinung zu [Thema]'],
        b2: ['Wie sinnvoll ist [Thema]?'],
        c1: ['Zwischen Nutzen und Risiko: [Thema]'],
      });
      break;
    case 'reference':
      fronts = taskRegister === 'informal'
        ? ['Danke fuer deine Nachricht zum Thema [Thema].', 'Ich habe deinen Vorschlag aufmerksam gelesen.']
        : [
            'Vielen Dank fuer Ihre Nachricht vom [Datum].',
            'Bezugnehmend auf Ihr Schreiben moechte ich wie folgt Stellung nehmen.',
          ];
      break;
    case 'introduction':
      if (discussionTask) {
        fronts = byLevel({
          b1: ['Ich moechte kurz meine Meinung zu diesem Thema sagen.'],
          b2: ['In der aktuellen Diskussion zu [Thema] halte ich folgenden Standpunkt fuer zentral.'],
          c1: ['Die gegenwaertige Debatte ueber [Thema] zeigt, wie komplex die Abwaegung zwischen den Positionen ist.'],
        });
      } else if (taskRegister === 'informal') {
        fronts = ['Danke fuer deine E-Mail.', 'Ich schreibe dir, weil ich dir zu [Thema] antworten moechte.'];
      } else {
        fronts = byLevel({
          b1: ['ich schreibe Ihnen, weil ich eine Rueckfrage zu [Thema] habe.'],
          b2: ['Hiermit moechte ich mich wegen [Thema] an Sie wenden.'],
          c1: ['Mit diesem Schreiben moechte ich den aktuellen Sachverhalt zu [Thema] praezisieren.'],
        });
      }
      break;
    case 'context':
      fronts = reportTask
        ? [
            'Im Rahmen des Projekts [Projektname] ergab sich folgender Handlungsbedarf.',
            'Aus dem bisherigen Verlauf laesst sich der folgende Hintergrund ableiten.',
          ]
        : ['Der aktuelle Kontext laesst sich wie folgt zusammenfassen: ...'];
      break;
    case 'analysis':
      fronts = byLevel({
        b1: ['Ein wichtiger Punkt ist, dass ...'],
        b2: ['Die vorliegenden Informationen zeigen deutlich, dass ...'],
        c1: ['Aus analytischer Sicht ist besonders hervorzuheben, dass ...'],
      });
      break;
    case 'request':
      fronts = taskRegister === 'informal'
        ? ['Kannst du mir bitte kurz bestaetigen, ob ...?', 'Bitte gib mir bis [Datum] Bescheid.']
        : ['Ich bitte Sie hoeflich, mir bis [Datum] Rueckmeldung zu geben.', 'Waere es moeglich, dass Sie [Massnahme] veranlassen?'];
      break;
    case 'recommendation':
      fronts = byLevel({
        b1: ['Ich schlage vor, dass wir als naechsten Schritt ...'],
        b2: ['Als praktikable Loesung empfehle ich, zunaechst ...'],
        c1: ['Aus den genannten Gruenden erscheint ein stufenweises Vorgehen als sinnvollste Option.'],
      });
      break;
    case 'appeal':
      fronts = [
        'Daher appelliere ich an alle Beteiligten, die langfristigen Folgen zu beruecksichtigen.',
        'Ich wuerde mir wuenschen, dass die Diskussion sachlich und loesungsorientiert gefuehrt wird.',
      ];
      break;
    case 'closing':
      if (discussionTask) {
        fronts = byLevel({
          b1: ['Zusammenfassend kann man sagen, dass ...'],
          b2: ['Abschliessend komme ich zu dem Ergebnis, dass ...'],
          c1: ['Abschliessend laesst sich festhalten, dass eine differenzierte Loesung notwendig ist.'],
        });
      } else if (taskRegister === 'informal') {
        fronts = ['Viele Gruesse', 'Liebe Gruesse'];
      } else {
        fronts = ['Ich danke Ihnen fuer Ihre Unterstuetzung und freue mich auf Ihre Rueckmeldung.', 'Mit freundlichen Gruessen'];
      }
      break;
    case 'signature':
      fronts = taskRegister === 'informal'
        ? ['Dein/Deine [Vorname]', '[Vorname]']
        : ['[Vorname] [Nachname]', 'Mit freundlichen Gruessen\n[Vorname] [Nachname]'];
      break;
    case 'senderaddress':
      fronts = ['[Vorname] [Nachname]\n[Musterstrasse 10]\n[12345 Musterstadt]'];
      break;
    case 'recipientaddress':
      fronts = ['[Firma/Institution]\nz. Hd. Frau/Herr [Name]\n[Adresse]'];
      break;
    case 'dateline':
      fronts = ['[Ort], [TT. Monat JJJJ]'];
      break;
    case 'mainbody':
    case 'contentpoint':
      if (reportTask) {
        fronts = [
          'Als zentrale Beobachtung laesst sich festhalten, dass ...',
          'Auf Grundlage der vorliegenden Daten empfehle ich als naechsten Schritt ...',
        ];
      } else if (complaintTask) {
        fronts = [
          'Leider entspricht [Sachverhalt] nicht den vereinbarten Bedingungen.',
          'Ich erwarte daher eine verbindliche Loesung bis spaetestens [Datum].',
        ];
      } else if (discussionTask) {
        fronts = byLevel({
          b1: ['Meiner Meinung nach ist [Aspekt] besonders wichtig.', 'Ein wichtiger Grund dafuer ist, dass ...'],
          b2: ['Einerseits spricht fuer [Position], dass ..., andererseits muss man bedenken, dass ...', 'Ein ueberzeugendes Argument besteht darin, dass ...'],
          c1: ['Nach sorgfaeltiger Abwaegung der Argumente zeigt sich, dass ...', 'Dieser Sichtweise laesst sich jedoch entgegenhalten, dass ...'],
        });
      } else if (taskRegister === 'informal') {
        fronts = ['Ausserdem wollte ich dir noch sagen, dass ...', 'Was haeltst du davon, wenn wir ...?'];
      } else {
        fronts = ['Darueber hinaus moechte ich betonen, dass ...', 'In diesem Zusammenhang halte ich folgende Massnahme fuer sinnvoll: ...'];
      }
      break;
    default:
      fronts = [];
  }

  // Create meaningful usage descriptions based on context
  const usageDescMap = {
    subject: taskRegister === 'informal' ? 'Casual subject line for personal communication' : 'Formal subject line for business or official correspondence',
    salutation: taskRegister === 'informal' ? 'Informal greeting for friends or colleagues' : 'Formal greeting for professional or official contexts',
    title: 'Essay or discussion topic title',
    reference: taskRegister === 'informal' ? 'Casual reference to previous informal message' : 'Formal reference to official correspondence',
    introduction: discussionTask ? 'Opening statement for discussion or debate' : 'Introduction to the letter or email purpose',
    context: reportTask ? 'Project or situation background context' : 'General context setting',
    analysis: 'Analytical statement analyzing the situation',
    request: taskRegister === 'informal' ? 'Casual request for favor or information' : 'Formal request for action or response',
    recommendation: 'Suggested solution or recommended course of action',
    appeal: 'Persuasive statement appealing to reader agreement',
    closing: discussionTask ? 'Summary conclusion for discussion' : 'Professional closing statement',
    signature: taskRegister === 'informal' ? 'Informal sign-off' : 'Formal signature block with full name',
    senderaddress: 'Return address for formal correspondence',
    recipientaddress: 'Mailing address for letter format',
    dateline: 'Date and place of writing',
    mainbody: reportTask ? 'Key finding or report observation' : complaintTask ? 'Statement of problem or complaint' : discussionTask ? 'Supporting argument for position' : 'Main content point',
    contentpoint: 'Supporting argument or content elaboration',
  };

  return fronts.map((front, index) => ({
    id: `generated_${examProfile || 'generic'}_${normalizedTaskType || 'task'}_${fieldKey}_${index + 1}`,
    front,
    back: '',
    usage: usageDescMap[fieldKey] || 'Contextual phrase for professional writing',
    context: generatedContext,
    sectionId: 'generated_fallback_coverage',
    sectionName: 'Fallback Coverage for Exam Preparation',
  }));
}

function sanitizePromptLabel(prompt = '') {
  return String(prompt).replace(/[:.\s]+$/g, '').trim();
}

function normalizeGuidingPoint(point) {
  if (typeof point === 'string') {
    return {
      prompt: '',
      text: point,
    };
  }

  return {
    prompt: String(point?.prompt || '').trim(),
    text: String(point?.text || '').trim(),
  };
}

function extractMatchTerms(value = '') {
  return Array.from(
    new Set(
      normalizeForMatch(value)
        .split(/[^a-z0-9]+/g)
        .map((term) => term.trim())
        .filter((term) => term.length >= 3 && !MATCH_STOPWORDS.has(term))
    )
  );
}

function extractScenarioBulletPoints(scenario = '') {
  const rawLines = String(scenario).split('\n');
  const numberedPoints = [];
  const bulletPoints = [];

  rawLines.forEach((rawLine) => {
    const line = rawLine.trim();
    if (!line) return;

    const numbered = line.match(/^\d+\.\s+(.+)/);
    const bulleted = line.match(/^[-*]\s+(.+)/);
    const captured = numbered?.[1] || bulleted?.[1];
    if (!captured) return;

    const cleaned = captured
      .replace(/[*_`>#]/g, '')
      .replace(/\s+/g, ' ')
      .trim();

    if (!cleaned || /^[A-D]$/i.test(cleaned)) return;

    if (numbered) numberedPoints.push(cleaned);
    else bulletPoints.push(cleaned);
  });

  if (numberedPoints.length > 0) return Array.from(new Set(numberedPoints));
  return Array.from(new Set(bulletPoints));
}

function inferTaskType(baseTaskType, activeTask) {
  const normalizedBase = String(baseTaskType || '').toLowerCase();
  if (normalizedBase !== 'choice-writing') return normalizedBase;

  const scenario = normalizeForMatch(activeTask?.scenario || '');

  if (scenario.includes('leserbrief')) return 'letter-to-the-editor';
  if (scenario.includes('bericht')) return 'internal-report';
  if (scenario.includes('aufsatz') || scenario.includes('essay') || scenario.includes('stellungnahme') || scenario.includes('kommentar')) {
    return 'choice-writing-essay';
  }
  if (scenario.includes('brief')) return 'formal-letter';
  if (scenario.includes('forum') || scenario.includes('diskussion')) return 'forum-post';

  return normalizedBase;
}

function buildGuidingPointFields(guidingPoints = [], t, options = {}) {
  const {
    fallbackPrefix = 'Content point',
    placeholderPrefix = 'Address this point:',
    contextKeywords = ['hauptteil', 'inhaltspunkt', 'argument'],
  } = options;

  const compactText = (value = '') => String(value).replace(/\s+/g, ' ').trim();

  const uniquePoints = [];
  const seenPointKeys = new Set();

  guidingPoints
    .map(normalizeGuidingPoint)
    .map((point) => ({
      prompt: compactText(point.prompt),
      text: compactText(point.text),
    }))
    .filter((point) => point.prompt || point.text)
    .forEach((point) => {
      const key = normalizeForMatch(`${sanitizePromptLabel(point.prompt)}|${point.text}`);
      if (!key || seenPointKeys.has(key)) return;
      seenPointKeys.add(key);
      uniquePoints.push(point);
    });

  return uniquePoints
    .map((point, index) => {
      const cleanPrompt = sanitizePromptLabel(point.prompt);
      const label = cleanPrompt || `${fallbackPrefix} ${index + 1}`;
      const hintParts = [];

      const promptNorm = normalizeForMatch(point.prompt);
      const textNorm = normalizeForMatch(point.text);
      const promptAndTextOverlap =
        promptNorm &&
        textNorm &&
        (promptNorm === textNorm || promptNorm.includes(textNorm) || textNorm.includes(promptNorm));

      if (point.prompt && point.text && promptAndTextOverlap) {
        hintParts.push(point.text.length >= point.prompt.length ? point.text : point.prompt);
      } else {
        if (point.prompt) hintParts.push(point.prompt);
        if (point.text) hintParts.push(point.text);
      }

      const concisePointText = compactText(point.text || cleanPrompt);
      const placeholderShouldIncludePoint = concisePointText && concisePointText.length <= 90;

      return {
        id: `contentPoint${index + 1}`,
        labelKey: `schreiben.structure.contentPoint${index + 1}`,
        defaultLabel: label,
        hintKey: `schreiben.structureHint.contentPoint${index + 1}`,
        defaultHint:
          hintParts.join(' ') ||
          t('schreiben.defaultContentPointHint', {
            defaultValue: 'Cover this required content point clearly.',
          }),
        placeholderKey: `schreiben.structurePlaceholder.contentPoint${index + 1}`,
        defaultPlaceholder: placeholderShouldIncludePoint
          ? `${placeholderPrefix} ${concisePointText}`
          : t('schreiben.defaultContentPointPlaceholder', {
              defaultValue: 'Write this required point...',
            }),
        rows: 4,
        contextKeywords: [
          ...contextKeywords,
          ...extractMatchTerms(point.prompt),
          ...extractMatchTerms(point.text),
        ].filter(Boolean),
      };
    });
}

function buildWritingStructure(taskType, activeTask, t, profile = {}) {
  const normalizedType = String(taskType || '').toLowerCase();
  const normalizedInputType = String(activeTask?.inputType || '').toLowerCase();
  const provider = String(profile?.provider || '').toLowerCase();
  const level = String(profile?.level || '').toLowerCase();
  const examProfile = `${provider}-${level}`;

  const scenarioFallbackPoints = extractScenarioBulletPoints(activeTask?.scenario || '');
  const rawGuidingPoints =
    Array.isArray(activeTask?.guidingPoints) && activeTask.guidingPoints.length > 0
      ? activeTask.guidingPoints
      : scenarioFallbackPoints;

  const guidingPointFields = buildGuidingPointFields(rawGuidingPoints, t, {
    fallbackPrefix: t('schreiben.contentPointLabel', { defaultValue: 'Content point' }),
    placeholderPrefix: t('schreiben.contentPointPlaceholderPrefix', { defaultValue: 'Address this point:' }),
    contextKeywords: ['hauptteil', 'inhaltspunkt', 'argument', 'begrundung', 'begruendung'],
  });

  const isDiscussionLike =
    DISCUSSION_TASK_TYPES.has(normalizedType) || FORUM_INPUT_TYPES.has(normalizedInputType);
  const isReportLike = REPORT_TASK_TYPES.has(normalizedType);
  const isEmailLike = EMAIL_TASK_TYPES.has(normalizedType);
  const isFormal =
    FORMAL_TASK_TYPES.has(normalizedType) ||
    normalizedType.includes('formal') ||
    normalizedType.includes('business') ||
    normalizedType.includes('professional');

  if (examProfile === 'telc-b2' && normalizedType === 'formal-letter') {
    const bodyFields =
      guidingPointFields.length > 0
        ? guidingPointFields
        : [
            {
              id: 'mainBody',
              labelKey: 'schreiben.structure.mainBody',
              defaultLabel: 'Main Body',
              hintKey: 'schreiben.structureHint.mainBody',
              defaultHint: 'Address all listed points from the chosen topic.',
              placeholderKey: 'schreiben.structurePlaceholder.mainBody',
              defaultPlaceholder: 'Firstly ..., in addition ..., therefore ...',
              rows: 9,
              contextKeywords: ['hauptteil', 'beschwerde', 'frage', 'forderung'],
            },
          ];

    return [
      {
        id: 'senderAddress',
        labelKey: 'schreiben.structure.senderAddress',
        defaultLabel: 'Absender / Sender address',
        hintKey: 'schreiben.structureHint.senderAddress',
        defaultHint: 'Write your sender block as required by TELC B2 formal letter format.',
        placeholderKey: 'schreiben.structurePlaceholder.senderAddress',
        defaultPlaceholder: 'Max Mustermann\nMusterstrasse 10\n12345 Musterstadt',
        rows: 4,
        contextKeywords: ['absender', 'adresse', 'briefkopf'],
      },
      {
        id: 'recipientAddress',
        labelKey: 'schreiben.structure.recipientAddress',
        defaultLabel: 'Anschrift / Recipient address',
        hintKey: 'schreiben.structureHint.recipientAddress',
        defaultHint: 'Add the recipient/company block as in a formal letter.',
        placeholderKey: 'schreiben.structurePlaceholder.recipientAddress',
        defaultPlaceholder: 'Secura Versicherungen AG\nz. Hd. Frau/Herr ...\nAdresse',
        rows: 4,
        contextKeywords: ['anschrift', 'empfaenger', 'firma'],
      },
      {
        id: 'dateLine',
        labelKey: 'schreiben.structure.dateLine',
        defaultLabel: 'Ort und Datum / Place and date',
        hintKey: 'schreiben.structureHint.dateLine',
        defaultHint: 'Use a proper place-date line.',
        placeholderKey: 'schreiben.structurePlaceholder.dateLine',
        defaultPlaceholder: 'Berlin, 14. Mai 2026',
        rows: 2,
        contextKeywords: ['datum', 'ort'],
      },
      {
        id: 'subject',
        labelKey: 'schreiben.structure.subject',
        defaultLabel: 'Betreff / Subject',
        hintKey: 'schreiben.structureHint.subject',
        defaultHint: 'State the formal purpose of your letter in one line.',
        placeholderKey: 'schreiben.structurePlaceholder.subject',
        defaultPlaceholder: 'Betreff: Anfrage zu ...',
        rows: 2,
        composePrefix: 'Betreff: ',
        contextKeywords: ['betreff', 'anliegen', 'zweck'],
      },
      {
        id: 'salutation',
        labelKey: 'schreiben.structure.salutation',
        defaultLabel: 'Anrede / Salutation',
        hintKey: 'schreiben.structureHint.salutation',
        defaultHint: 'Use a formal salutation suitable for a business context.',
        placeholderKey: 'schreiben.structurePlaceholder.salutation',
        defaultPlaceholder: 'Sehr geehrte Damen und Herren,',
        rows: 2,
        contextKeywords: ['anrede', 'formell', 'sehr geehrte'],
      },
      {
        id: 'introduction',
        labelKey: 'schreiben.structure.introduction',
        defaultLabel: 'Einleitung / Opening',
        hintKey: 'schreiben.structureHint.introduction',
        defaultHint: 'Briefly explain why you are writing.',
        placeholderKey: 'schreiben.structurePlaceholder.introduction',
        defaultPlaceholder: 'Hiermit moechte ich ...',
        rows: 3,
        contextKeywords: ['einleitung', 'anlass', 'grund'],
      },
      ...bodyFields,
      {
        id: 'closing',
        labelKey: 'schreiben.structure.closing',
        defaultLabel: 'Schlussformel / Closing',
        hintKey: 'schreiben.structureHint.closing',
        defaultHint: 'Close politely and formally.',
        placeholderKey: 'schreiben.structurePlaceholder.closing',
        defaultPlaceholder: 'Mit freundlichen Gruessen',
        rows: 2,
        contextKeywords: ['schluss', 'gruss', 'abschluss'],
      },
      {
        id: 'signature',
        labelKey: 'schreiben.structure.signature',
        defaultLabel: 'Name / Signature',
        hintKey: 'schreiben.structureHint.signature',
        defaultHint: 'Sign with your full name.',
        placeholderKey: 'schreiben.structurePlaceholder.signature',
        defaultPlaceholder: 'Max Mustermann',
        rows: 2,
        contextKeywords: ['name', 'unterschrift'],
      },
    ];
  }

  if (examProfile === 'osd-c1' && normalizedType === 'formal-response-email') {
    const bodyFields =
      guidingPointFields.length > 0
        ? guidingPointFields
        : [
            {
              id: 'mainBody',
              labelKey: 'schreiben.structure.mainBody',
              defaultLabel: 'Main response',
              hintKey: 'schreiben.structureHint.mainBody',
              defaultHint: 'Respond to all notes in a formal and polite register.',
              placeholderKey: 'schreiben.structurePlaceholder.mainBody',
              defaultPlaceholder: 'Vielen Dank fuer Ihre Nachricht. Hierzu moechte ich folgendes anmerken: ...',
              rows: 9,
              contextKeywords: ['antwort', 'notiz', 'erklaerung', 'vorschlag'],
            },
          ];

    return [
      {
        id: 'subject',
        labelKey: 'schreiben.structure.subject',
        defaultLabel: 'Betreff / Subject',
        hintKey: 'schreiben.structureHint.subject',
        defaultHint: 'Use a clear formal reference line.',
        placeholderKey: 'schreiben.structurePlaceholder.subject',
        defaultPlaceholder: 'Betreff: Ihre Nachricht zur Anmeldung',
        rows: 2,
        composePrefix: 'Betreff: ',
        contextKeywords: ['betreff', 'antwort', 'anmeldung'],
      },
      {
        id: 'salutation',
        labelKey: 'schreiben.structure.salutation',
        defaultLabel: 'Anrede / Salutation',
        hintKey: 'schreiben.structureHint.salutation',
        defaultHint: 'Start with a formal salutation.',
        placeholderKey: 'schreiben.structurePlaceholder.salutation',
        defaultPlaceholder: 'Sehr geehrte Frau Knaller,',
        rows: 2,
        contextKeywords: ['anrede', 'formell', 'sehr geehrte'],
      },
      {
        id: 'reference',
        labelKey: 'schreiben.structure.reference',
        defaultLabel: 'Bezug auf die Nachricht',
        hintKey: 'schreiben.structureHint.reference',
        defaultHint: 'Reference the original message and set the context politely.',
        placeholderKey: 'schreiben.structurePlaceholder.reference',
        defaultPlaceholder: 'Vielen Dank fuer Ihre E-Mail vom ...',
        rows: 3,
        contextKeywords: ['bezug', 'nachricht', 'vielen dank'],
      },
      ...bodyFields,
      {
        id: 'request',
        labelKey: 'schreiben.structure.request',
        defaultLabel: 'Bitte / Vorschlag',
        hintKey: 'schreiben.structureHint.request',
        defaultHint: 'State your concrete request or alternative proposal.',
        placeholderKey: 'schreiben.structurePlaceholder.request',
        defaultPlaceholder: 'Ich bitte Sie daher um ...',
        rows: 4,
        contextKeywords: ['bitte', 'vorschlag', 'anfrage', 'alternative'],
      },
      {
        id: 'closing',
        labelKey: 'schreiben.structure.closing',
        defaultLabel: 'Schlussformel / Closing',
        hintKey: 'schreiben.structureHint.closing',
        defaultHint: 'Close formally and politely.',
        placeholderKey: 'schreiben.structurePlaceholder.closing',
        defaultPlaceholder: 'Mit freundlichen Gruessen',
        rows: 2,
        contextKeywords: ['schluss', 'gruss', 'abschluss'],
      },
      {
        id: 'signature',
        labelKey: 'schreiben.structure.signature',
        defaultLabel: 'Name / Signature',
        hintKey: 'schreiben.structureHint.signature',
        defaultHint: 'Sign with your full name.',
        placeholderKey: 'schreiben.structurePlaceholder.signature',
        defaultPlaceholder: 'Max Mustermann',
        rows: 2,
        contextKeywords: ['name', 'unterschrift'],
      },
    ];
  }

  if (
    examProfile === 'telc-c1' &&
    ['email-opinion', 'email-response-community', 'email-response-professional'].includes(normalizedType)
  ) {
    const bodyFields =
      guidingPointFields.length > 0
        ? guidingPointFields
        : [
            {
              id: 'mainBody',
              labelKey: 'schreiben.structure.mainBody',
              defaultLabel: 'Main body',
              hintKey: 'schreiben.structureHint.mainBody',
              defaultHint: 'Address all required C1 content points clearly and coherently.',
              placeholderKey: 'schreiben.structurePlaceholder.mainBody',
              defaultPlaceholder: 'Ich halte den Vorschlag grundsaetzlich fuer ..., da ...',
              rows: 9,
              contextKeywords: ['meinung', 'bewertung', 'vorschlag', 'begruendung'],
            },
          ];

    return [
      {
        id: 'subject',
        labelKey: 'schreiben.structure.subject',
        defaultLabel: 'Betreff / Subject',
        hintKey: 'schreiben.structureHint.subject',
        defaultHint: 'Use a concise and relevant subject line.',
        placeholderKey: 'schreiben.structurePlaceholder.subject',
        defaultPlaceholder: 'Betreff: Ihre Initiative / Ihr Vorschlag',
        rows: 2,
        composePrefix: 'Betreff: ',
        contextKeywords: ['betreff', 'vorschlag', 'initiative'],
      },
      {
        id: 'salutation',
        labelKey: 'schreiben.structure.salutation',
        defaultLabel: 'Anrede / Salutation',
        hintKey: 'schreiben.structureHint.salutation',
        defaultHint: 'Choose a fitting register for the relationship context.',
        placeholderKey: 'schreiben.structurePlaceholder.salutation',
        defaultPlaceholder: 'Liebe Frau Bauer,',
        rows: 2,
        contextKeywords: ['anrede', 'beziehung', 'hoeflichkeitsform'],
      },
      {
        id: 'reference',
        labelKey: 'schreiben.structure.reference',
        defaultLabel: 'Bezug / Opening context',
        hintKey: 'schreiben.structureHint.reference',
        defaultHint: 'Reference the received email and transition into your response.',
        placeholderKey: 'schreiben.structurePlaceholder.reference',
        defaultPlaceholder: 'Vielen Dank fuer Ihre E-Mail und den interessanten Vorschlag.',
        rows: 3,
        contextKeywords: ['bezug', 'e-mail', 'vielen dank'],
      },
      ...bodyFields,
      {
        id: 'closing',
        labelKey: 'schreiben.structure.closing',
        defaultLabel: 'Gruss / Closing',
        hintKey: 'schreiben.structureHint.closing',
        defaultHint: 'Close in a register appropriate way.',
        placeholderKey: 'schreiben.structurePlaceholder.closing',
        defaultPlaceholder: 'Herzliche Gruesse',
        rows: 2,
        contextKeywords: ['schluss', 'gruss', 'abschluss'],
      },
      {
        id: 'signature',
        labelKey: 'schreiben.structure.signature',
        defaultLabel: 'Name / Signature',
        hintKey: 'schreiben.structureHint.signature',
        defaultHint: 'Sign with your name.',
        placeholderKey: 'schreiben.structurePlaceholder.signature',
        defaultPlaceholder: 'Max Mustermann',
        rows: 2,
        contextKeywords: ['name', 'unterschrift'],
      },
    ];
  }

  if (normalizedType === 'letter-to-the-editor') {
    const bodyFields =
      guidingPointFields.length > 0
        ? guidingPointFields
        : [
            {
              id: 'mainBody',
              labelKey: 'schreiben.structure.mainBody',
              defaultLabel: 'Main argumentation',
              hintKey: 'schreiben.structureHint.mainBody',
              defaultHint: 'React to the article and argue your position with examples.',
              placeholderKey: 'schreiben.structurePlaceholder.mainBody',
              defaultPlaceholder: 'Ich moechte dem Artikel widersprechen, weil ...',
              rows: 8,
              contextKeywords: ['argument', 'begruendung', 'beispiel', 'stellungnahme'],
            },
          ];

    return [
      {
        id: 'title',
        labelKey: 'schreiben.structure.title',
        defaultLabel: 'Title / Topic',
        hintKey: 'schreiben.structureHint.title',
        defaultHint: 'Name the debated issue clearly.',
        placeholderKey: 'schreiben.structurePlaceholder.title',
        defaultPlaceholder: 'Zum Kommentar ueber ...',
        rows: 2,
        contextKeywords: ['titel', 'thema', 'kommentar'],
      },
      {
        id: 'reference',
        labelKey: 'schreiben.structure.reference',
        defaultLabel: 'Bezug auf den Artikel',
        hintKey: 'schreiben.structureHint.reference',
        defaultHint: 'Briefly mention the article/comment you are responding to.',
        placeholderKey: 'schreiben.structurePlaceholder.reference',
        defaultPlaceholder: 'In Ihrem Artikel vom ... wird behauptet, dass ...',
        rows: 3,
        contextKeywords: ['artikel', 'bezug', 'zeitung'],
      },
      ...bodyFields,
      {
        id: 'appeal',
        labelKey: 'schreiben.structure.appeal',
        defaultLabel: 'Appell / Closing appeal',
        hintKey: 'schreiben.structureHint.appeal',
        defaultHint: 'End with a concrete appeal to readers or institutions.',
        placeholderKey: 'schreiben.structurePlaceholder.appeal',
        defaultPlaceholder: 'Daher appelliere ich an ...',
        rows: 3,
        contextKeywords: ['appell', 'aufruf', 'schluss'],
      },
      {
        id: 'signature',
        labelKey: 'schreiben.structure.signature',
        defaultLabel: 'Name / Signature',
        hintKey: 'schreiben.structureHint.signature',
        defaultHint: 'Sign as a letter writer.',
        placeholderKey: 'schreiben.structurePlaceholder.signature',
        defaultPlaceholder: 'Max Mustermann',
        rows: 2,
        contextKeywords: ['name', 'unterschrift'],
      },
    ];
  }

  if (['internal-report', 'internal-summary-recommendation'].includes(normalizedType)) {
    const bodyFields =
      guidingPointFields.length > 0
        ? guidingPointFields
        : [
            {
              id: 'analysis',
              labelKey: 'schreiben.structure.analysis',
              defaultLabel: 'Analysis / Key points',
              hintKey: 'schreiben.structureHint.analysis',
              defaultHint: 'Summarize relevant findings objectively.',
              placeholderKey: 'schreiben.structurePlaceholder.analysis',
              defaultPlaceholder: 'Zentrale Ergebnisse: ...',
              rows: 7,
              contextKeywords: ['analyse', 'ergebnis', 'herausforderung'],
            },
          ];

    return [
      {
        id: 'subject',
        labelKey: 'schreiben.structure.subject',
        defaultLabel: 'Betreff / Subject',
        hintKey: 'schreiben.structureHint.subject',
        defaultHint: 'State report purpose in one concise line.',
        placeholderKey: 'schreiben.structurePlaceholder.subject',
        defaultPlaceholder: 'Betreff: Kurzbericht zum Projekt ...',
        rows: 2,
        composePrefix: 'Betreff: ',
        contextKeywords: ['betreff', 'memo', 'bericht'],
      },
      {
        id: 'context',
        labelKey: 'schreiben.structure.context',
        defaultLabel: 'Context / Background',
        hintKey: 'schreiben.structureHint.context',
        defaultHint: 'Briefly explain project context and objective.',
        placeholderKey: 'schreiben.structurePlaceholder.context',
        defaultPlaceholder: 'Im Rahmen des Projekts ...',
        rows: 4,
        contextKeywords: ['kontext', 'hintergrund', 'ziel'],
      },
      ...bodyFields,
      {
        id: 'recommendation',
        labelKey: 'schreiben.structure.recommendation',
        defaultLabel: 'Recommendation / Next steps',
        hintKey: 'schreiben.structureHint.recommendation',
        defaultHint: 'Offer a concrete follow-up recommendation.',
        placeholderKey: 'schreiben.structurePlaceholder.recommendation',
        defaultPlaceholder: 'Empfehlung: Fuer das Folgeprojekt sollte ...',
        rows: 4,
        contextKeywords: ['empfehlung', 'naechste schritte', 'vorschlag'],
      },
      {
        id: 'signature',
        labelKey: 'schreiben.structure.signature',
        defaultLabel: 'Name / Signature',
        hintKey: 'schreiben.structureHint.signature',
        defaultHint: 'Sign with name and role if needed.',
        placeholderKey: 'schreiben.structurePlaceholder.signature',
        defaultPlaceholder: 'Max Mustermann, Projektleitung',
        rows: 2,
        contextKeywords: ['name', 'rolle', 'unterschrift'],
      },
    ];
  }

  if (isDiscussionLike) {
    const needsForumReference =
      FORUM_INPUT_TYPES.has(normalizedInputType) ||
      ['forum-post', 'academic-forum-post'].includes(normalizedType);

    const bodyFields =
      guidingPointFields.length > 0
        ? guidingPointFields
        : [
            {
              id: 'mainBody',
              labelKey: 'schreiben.structure.mainBody',
              defaultLabel: 'Main Body',
              hintKey: 'schreiben.structureHint.mainBody',
              defaultHint: 'Develop your arguments and cover all required points.',
              placeholderKey: 'schreiben.structurePlaceholder.mainBody',
              defaultPlaceholder: 'First ..., in addition ..., however ...',
              rows: 9,
              contextKeywords: ['hauptteil', 'argument', 'begrundung', 'begruendung', 'vorteil', 'nachteil'],
            },
          ];

    const introFields = [
      {
        id: 'title',
        labelKey: 'schreiben.structure.title',
        defaultLabel: 'Title / Topic',
        hintKey: 'schreiben.structureHint.title',
        defaultHint: 'Summarize your position in one short line.',
        placeholderKey: 'schreiben.structurePlaceholder.title',
        defaultPlaceholder: 'e.g. Personal contacts and social media',
        rows: 2,
        contextKeywords: ['titel', 'thema', 'meinung'],
      },
    ];

    if (needsForumReference) {
      introFields.push({
        id: 'reference',
        labelKey: 'schreiben.structure.reference',
        defaultLabel: 'Reference to statement',
        hintKey: 'schreiben.structureHint.reference',
        defaultHint: 'Briefly refer to the post/comment you are responding to.',
        placeholderKey: 'schreiben.structurePlaceholder.reference',
        defaultPlaceholder: 'Im Beitrag von ... wird gesagt, dass ...',
        rows: 3,
        contextKeywords: ['bezug', 'beitrag', 'meinung', 'stellungnahme'],
      });
    }

    introFields.push({
      id: 'introduction',
      labelKey: 'schreiben.structure.introduction',
      defaultLabel: 'Introduction',
      hintKey: 'schreiben.structureHint.introduction',
      defaultHint: 'Introduce the topic and your position clearly.',
      placeholderKey: 'schreiben.structurePlaceholder.introduction',
      defaultPlaceholder: 'In my opinion, ...',
      rows: 4,
      contextKeywords: ['einleitung', 'position', 'meinung', 'these'],
    });

    return [
      ...introFields,
      ...bodyFields,
      {
        id: 'closing',
        labelKey: 'schreiben.structure.closing',
        defaultLabel: 'Conclusion / Closing Line',
        hintKey: 'schreiben.structureHint.closing',
        defaultHint: 'Close with a clear final thought or recommendation.',
        placeholderKey: 'schreiben.structurePlaceholder.closing',
        defaultPlaceholder: 'To sum up, ...',
        rows: 3,
        contextKeywords: ['schluss', 'fazit', 'empfehlung', 'abschluss'],
      },
    ];
  }

  if (isReportLike) {
    const bodyFields =
      guidingPointFields.length > 0
        ? guidingPointFields
        : [
            {
              id: 'analysis',
              labelKey: 'schreiben.structure.analysis',
              defaultLabel: 'Analysis / Key Points',
              hintKey: 'schreiben.structureHint.analysis',
              defaultHint: 'Summarize relevant findings and evidence clearly.',
              placeholderKey: 'schreiben.structurePlaceholder.analysis',
              defaultPlaceholder: 'Current situation: ...',
              rows: 8,
              contextKeywords: ['analyse', 'situation', 'problem', 'ursache'],
            },
          ];

    return [
      {
        id: 'subject',
        labelKey: 'schreiben.structure.subject',
        defaultLabel: 'Betreff / Subject',
        hintKey: 'schreiben.structureHint.subject',
        defaultHint: 'Short and precise topic line.',
        placeholderKey: 'schreiben.structurePlaceholder.subject',
        defaultPlaceholder: 'Betreff: Bericht zu ...',
        rows: 2,
        composePrefix: 'Betreff: ',
        contextKeywords: ['betreff', 'thema', 'anliegen'],
      },
      {
        id: 'salutation',
        labelKey: 'schreiben.structure.salutation',
        defaultLabel: 'Anrede / Salutation',
        hintKey: 'schreiben.structureHint.salutation',
        defaultHint: 'Use a professional and polite salutation.',
        placeholderKey: 'schreiben.structurePlaceholder.salutation',
        defaultPlaceholder: 'Sehr geehrte Frau ...',
        rows: 2,
        contextKeywords: ['anrede', 'hoeflich', 'sehr geehrte'],
      },
      {
        id: 'context',
        labelKey: 'schreiben.structure.context',
        defaultLabel: 'Context / Background',
        hintKey: 'schreiben.structureHint.context',
        defaultHint: 'State the context and why this report/message is needed.',
        placeholderKey: 'schreiben.structurePlaceholder.context',
        defaultPlaceholder: 'I am writing regarding ...',
        rows: 4,
        contextKeywords: ['kontext', 'hintergrund', 'anlass'],
      },
      ...bodyFields,
      {
        id: 'recommendation',
        labelKey: 'schreiben.structure.recommendation',
        defaultLabel: 'Recommendation / Next Steps',
        hintKey: 'schreiben.structureHint.recommendation',
        defaultHint: 'Suggest clear and realistic next steps.',
        placeholderKey: 'schreiben.structurePlaceholder.recommendation',
        defaultPlaceholder: 'I recommend that ...',
        rows: 4,
        contextKeywords: ['empfehlung', 'vorschlag', 'naechste schritte', 'loesung'],
      },
      {
        id: 'closing',
        labelKey: 'schreiben.structure.closing',
        defaultLabel: 'Gruß / Closing',
        hintKey: 'schreiben.structureHint.closing',
        defaultHint: 'Close politely and professionally.',
        placeholderKey: 'schreiben.structurePlaceholder.closing',
        defaultPlaceholder: 'Mit freundlichen Gruessen',
        rows: 2,
        contextKeywords: ['gruss', 'schluss', 'abschluss'],
      },
      {
        id: 'signature',
        labelKey: 'schreiben.structure.signature',
        defaultLabel: 'Name / Signature',
        hintKey: 'schreiben.structureHint.signature',
        defaultHint: 'Sign with your name.',
        placeholderKey: 'schreiben.structurePlaceholder.signature',
        defaultPlaceholder: 'Maria Schmidt',
        rows: 2,
        contextKeywords: ['name', 'unterschrift'],
      },
    ];
  }

  if (isEmailLike) {
    const bodyFields =
      guidingPointFields.length > 0
        ? guidingPointFields
        : [
            {
              id: 'mainBody',
              labelKey: 'schreiben.structure.mainBody',
              defaultLabel: 'Hauptteil / Main Body',
              hintKey: 'schreiben.structureHint.mainBody',
              defaultHint: 'Cover all guiding points in a logical order.',
              placeholderKey: 'schreiben.structurePlaceholder.mainBody',
              defaultPlaceholder: 'Erstens ..., ausserdem ..., zum Schluss ...',
              rows: 9,
              contextKeywords: ['hauptteil', 'fragen', 'bitte', 'vorschlag', 'argument'],
            },
          ];

    return [
      {
        id: 'subject',
        labelKey: 'schreiben.structure.subject',
        defaultLabel: 'Betreff / Subject',
        hintKey: 'schreiben.structureHint.subject',
        defaultHint: 'Short and precise topic line.',
        placeholderKey: 'schreiben.structurePlaceholder.subject',
        defaultPlaceholder: 'Betreff: Anfrage zu ...',
        rows: 2,
        composePrefix: 'Betreff: ',
        contextKeywords: ['betreff', 'thema', 'anliegen'],
      },
      {
        id: 'salutation',
        labelKey: 'schreiben.structure.salutation',
        defaultLabel: 'Anrede / Salutation',
        hintKey: 'schreiben.structureHint.salutation',
        defaultHint: isFormal
          ? 'Use formal address (e.g. Sehr geehrte Frau ...).'
          : 'Use friendly but correct address (e.g. Liebe ...).',
        placeholderKey: 'schreiben.structurePlaceholder.salutation',
        defaultPlaceholder: isFormal ? 'Sehr geehrte Damen und Herren,' : 'Liebe Anna,',
        rows: 2,
        contextKeywords: ['anrede', 'hoeflich', 'informell', 'formal'],
      },
      {
        id: 'introduction',
        labelKey: 'schreiben.structure.introduction',
        defaultLabel: 'Introduction',
        hintKey: 'schreiben.structureHint.introduction',
        defaultHint: 'State why you are writing and set context.',
        placeholderKey: 'schreiben.structurePlaceholder.introduction',
        defaultPlaceholder: 'Ich schreibe Ihnen, weil ...',
        rows: 3,
        contextKeywords: ['einleitung', 'anlass', 'grund des schreibens'],
      },
      ...bodyFields,
      {
        id: 'closing',
        labelKey: 'schreiben.structure.closing',
        defaultLabel: 'Gruß / Closing',
        hintKey: 'schreiben.structureHint.closing',
        defaultHint: 'Use an appropriate closing line for the register.',
        placeholderKey: 'schreiben.structurePlaceholder.closing',
        defaultPlaceholder: isFormal ? 'Mit freundlichen Gruessen' : 'Viele Gruesse',
        rows: 2,
        contextKeywords: ['schluss', 'gruss', 'abschluss'],
      },
      {
        id: 'signature',
        labelKey: 'schreiben.structure.signature',
        defaultLabel: 'Name / Signature',
        hintKey: 'schreiben.structureHint.signature',
        defaultHint: 'Sign with a suitable name.',
        placeholderKey: 'schreiben.structurePlaceholder.signature',
        defaultPlaceholder: 'Maria Schmidt',
        rows: 2,
        contextKeywords: ['name', 'unterschrift'],
      },
    ];
  }

  return [
    {
      id: 'introduction',
      labelKey: 'schreiben.structure.introduction',
      defaultLabel: 'Introduction',
      hintKey: 'schreiben.structureHint.introduction',
      defaultHint: 'Start with clear context.',
      placeholderKey: 'schreiben.structurePlaceholder.introduction',
      defaultPlaceholder: 'I am writing because ...',
      rows: 4,
      contextKeywords: ['einleitung', 'kontext'],
    },
    {
      id: 'mainBody',
      labelKey: 'schreiben.structure.mainBody',
      defaultLabel: 'Main Body',
      hintKey: 'schreiben.structureHint.mainBody',
      defaultHint: 'Cover the required points.',
      placeholderKey: 'schreiben.structurePlaceholder.mainBody',
      defaultPlaceholder: 'Main arguments and details ...',
      rows: 10,
      contextKeywords: ['hauptteil', 'inhaltspunkt'],
    },
    {
      id: 'closing',
      labelKey: 'schreiben.structure.closing',
      defaultLabel: 'Closing',
      hintKey: 'schreiben.structureHint.closing',
      defaultHint: 'End clearly and politely.',
      placeholderKey: 'schreiben.structurePlaceholder.closing',
      defaultPlaceholder: 'Kind regards',
      rows: 3,
      contextKeywords: ['schluss', 'abschluss'],
    },
  ];
}

function normalizeForMatch(value = '') {
  return String(value)
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/ß/g, 'ss')
    .replace(/\be[\s-]?mails?\b/g, 'email')
    .replace(/[^a-z0-9]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function countWords(text = '') {
  return text
    .trim()
    .split(/\s+/)
    .filter(Boolean).length;
}

function getInitialPartDraft(part) {
  const hasTaskChoice = !!(part?.optional && Array.isArray(part?.tasks) && part.tasks.length > 1);
  return {
    selectedTaskIndex: hasTaskChoice ? null : 0,
    fields: {},
    taskFields: {},
    openHints: {},
  };
}

function dedupeRedemittelCards(cards = []) {
  const seen = new Set();
  const uniqueCards = [];

  cards.forEach((card) => {
    if (!card) return;

    const stableKey =
      card.id ||
      `${normalizeForMatch(card.front || '')}|${normalizeForMatch(card.context || '')}|${normalizeForMatch(card.usage || '')}`;

    if (seen.has(stableKey)) return;
    seen.add(stableKey);
    uniqueCards.push(card);
  });

  return uniqueCards;
}

function buildWordGoalLabel(wordLimit, t) {
  if (!wordLimit) return null;

  if (typeof wordLimit?.type === 'string' && wordLimit.type.toLowerCase() === 'detailed') {
    return t('schreiben.wordGoalDetailed', {
      defaultValue: 'Detailed response expected',
    });
  }

  if (typeof wordLimit?.min === 'number') {
    return t('schreiben.wordGoalMin', {
      defaultValue: 'At least {{count}} words',
      count: wordLimit.min,
    });
  }

  if (typeof wordLimit?.target === 'number') {
    return t('schreiben.wordGoalTarget', {
      defaultValue: 'Around {{count}} words',
      count: wordLimit.target,
    });
  }

  if (Array.isArray(wordLimit?.range) && wordLimit.range.length === 2) {
    return t('schreiben.wordGoalRange', {
      defaultValue: '{{min}}-{{max}} words',
      min: wordLimit.range[0],
      max: wordLimit.range[1],
    });
  }

  return null;
}

function flattenWritingRedemittelCards(data, profileHint = {}) {
  const provider = String(data?.provider || profileHint?.provider || '').toLowerCase();
  const level = String(data?.level || profileHint?.level || '').toLowerCase();
  const examProfile = provider && level ? `${provider}-${level}` : '';
  const parts = data?.skills?.schreiben?.parts || [];

  return parts.flatMap((part) =>
    (part.cards || []).map((card) => ({
      ...card,
      sectionId: part.id,
      sectionName: part.name,
      examProfile,
    }))
  );
}

function getMatchingRedemittel(cards, field, activeTask, taskType, profile = {}) {
  if (!Array.isArray(cards) || cards.length === 0) return [];

  const rawType = String(taskType || activeTask?.taskType || '').toLowerCase();
  const provider = normalizeForMatch(profile?.provider || '');
  const level = normalizeForMatch(profile?.level || '');
  const examProfile = provider && level ? `${provider}-${level}` : '';
  const taskRegister = inferTaskRegister(activeTask, rawType, examProfile);
  const allowedSectionIds = getAllowedSectionIds(examProfile, rawType, taskRegister);

  const fieldKey = normalizeFieldKey(field?.id || '');
  const roleTargets = getFieldRoleTargets(field?.id || '');

  const fieldAliasTerms = FIELD_REDEMITTEL_CONTEXT_ALIASES[field?.id] || [];
  const taskAliasTerms = TASK_REDEMITTEL_CONTEXT_ALIASES[rawType] || [];
  const profileAliasTerms = PROFILE_REDEMITTEL_CONTEXT_ALIASES[examProfile] || [];

  const guidingPointTerms = Array.isArray(activeTask?.guidingPoints)
    ? activeTask.guidingPoints.flatMap((point) => {
        const normalizedPoint = normalizeGuidingPoint(point);
        return [normalizedPoint.prompt, normalizedPoint.text];
      })
    : [];

  const keywords = Array.from(
    new Set(
      [
        ...(field?.contextKeywords || []),
        ...fieldAliasTerms,
        ...taskAliasTerms,
        ...profileAliasTerms,
        taskType,
        activeTask?.taskType,
        activeTask?.inputType,
        activeTask?.scenario,
        ...guidingPointTerms,
      ].flatMap((entry) => extractMatchTerms(entry))
    )
  ).slice(0, 80);

  const scored = cards
    .map((card) => {
      const cardRoles = inferCardRoles(card);
      if (cardRoles.includes('exclude')) return null;

      if (Array.isArray(allowedSectionIds) && allowedSectionIds.length > 0) {
        if (!allowedSectionIds.includes(card.sectionId)) return null;
      }

      const roleTargetIndex = roleTargets.findIndex((role) => cardRoles.includes(role));
      if (roleTargetIndex < 0) return null;

      const cardRegister = inferCardRegister(card);
      if (!isRegisterCompatible(taskRegister, cardRegister, cardRoles, fieldKey)) return null;

      const context = normalizeForMatch(card.context || '');
      const usage = normalizeForMatch(card.usage || '');
      const front = normalizeForMatch(card.front || '');
      const sectionName = normalizeForMatch(card.sectionName || '');
      const haystack = `${context} ${usage} ${front} ${sectionName}`;

      let score = 8;
      let fieldScore = 1;

      // Strong role-first scoring keeps each phrase inside the exact block type.
      if (roleTargetIndex === 0) {
        score += 8;
        fieldScore += 3;
      } else {
        score += Math.max(2, 6 - roleTargetIndex);
        fieldScore += 2;
      }

      if (cardRoles.includes('signature') && fieldKey === 'signature') {
        score += 5;
      }

      if (cardRoles.includes('closing') && fieldKey === 'closing') {
        score += 4;
      }

      if (cardRoles.includes('mainBody') && ['mainbody', 'contentpoint', 'analysis'].includes(fieldKey)) {
        score += 3;
      }

      fieldAliasTerms.forEach((alias) => {
        const term = normalizeForMatch(alias);
        if (!term) return;

        if (context.includes(term) || sectionName.includes(term)) {
          score += 5;
          fieldScore += 2;
        } else if (haystack.includes(term)) {
          score += 3;
          fieldScore += 1;
        }
      });

      keywords.forEach((keyword) => {
        if (haystack.includes(keyword)) score += keyword.length >= 8 ? 3 : 2;
      });

      if (fieldKey === 'salutation' && /anrede|sehr geehrte|liebe|hallo/.test(haystack)) score += 4;
      if (fieldKey === 'subject' && /betreff|thema|anliegen/.test(haystack)) score += 4;
      if (fieldKey === 'reference' && /bezug|beitrag|artikel|nachricht|kommentar/.test(haystack)) score += 4;
      if (fieldKey === 'introduction' && /einleitung|grund|anlass/.test(haystack)) score += 3;
      if (fieldKey === 'request' && /bitte|frage|forderung/.test(haystack)) score += 3;
      if (fieldKey === 'analysis' && /analyse|problem|ursache|situation/.test(haystack)) score += 3;
      if (fieldKey === 'recommendation' && /vorschlag|empfehl|sollte|koennte/.test(haystack)) score += 3;
      if (fieldKey === 'appeal' && /appell|aufruf|sollte|muesste/.test(haystack)) score += 3;
      if (fieldKey === 'title' && /titel|thema|standpunkt/.test(haystack)) score += 2;
      if (fieldKey === 'dateline' && /datum|ort/.test(haystack)) score += 2;

      // Slightly down-rank cards that never matched the active block context.
      if (fieldScore === 0) score -= 2;

      return {
        cardRegister,
        cardRoles,
        card,
        score,
        fieldScore,
      };
    })
    .filter((entry) => entry && entry.score > 0)
    .sort((a, b) => {
      if (b.fieldScore !== a.fieldScore) return b.fieldScore - a.fieldScore;
      return b.score - a.score;
    });

  const strictMatches = scored
    .filter((entry) => entry.fieldScore >= 2 || entry.score >= 15)
    .map((entry) => entry.card);

  const matchedCards = dedupeRedemittelCards(strictMatches).slice(0, 10);
  const generatedFallback = buildGeneratedRedemittelCards({
    examProfile,
    field,
    taskRegister,
    taskType: rawType,
  });

  if (matchedCards.length >= 3) return matchedCards;

  const combined = dedupeRedemittelCards([...matchedCards, ...generatedFallback]).slice(0, 10);
  if (combined.length > 0) return combined;

  // If a block still has no dedicated phrase, keep deterministic generic fallbacks.
  return dedupeRedemittelCards(generatedFallback).slice(0, 10);
}

function composeStructuredText(structure, values) {
  if (!Array.isArray(structure)) return '';

  const blocks = structure
    .map((field) => {
      const value = String(values?.[field.id] || '').trim();
      if (!value) return '';
      if (field.composePrefix) return `${field.composePrefix}${value}`;
      return value;
    })
    .filter(Boolean);

  return blocks.join('\n\n');
}

function AnimatedBackButton({ onClick, backLabel = 'Zurueck' }) {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className="relative overflow-hidden inline-flex items-center justify-center gap-2 rounded-xl border border-[#E1E4E8] bg-white px-3.5 py-2.5 min-h-[48px] shadow-[0_4px_20px_rgba(0,0,0,0.05)] transition-all duration-300 hover:shadow-lg touch-manipulation focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#3A86FF]/35"
      aria-label={backLabel}
      type="button"
    >
      <div
        style={{
          position: 'absolute',
          top: 0,
          bottom: 0,
          right: 0,
          width: isHovered ? '100%' : '0%',
          background: 'linear-gradient(to left, #9ca3af, #4b5563, #000000)',
          transition: 'width 0.7s ease-out',
          zIndex: 0,
        }}
      />
      <ArrowLeft
        className="w-4 h-4 relative z-10 transition-colors duration-500"
        style={{ color: isHovered ? '#ffffff' : '#000000' }}
      />
      <span
        className="hidden sm:inline font-semibold text-sm relative z-10 transition-colors duration-500"
        style={{ color: isHovered ? '#ffffff' : '#000000' }}
      >
        {backLabel}
      </span>
    </button>
  );
}

function Stopwatch({ sw, t }) {
  return (
    <div className="flex items-center gap-2">
      <button
        onClick={sw.toggle}
        className={`min-h-[48px] inline-flex items-center gap-2 px-3.5 py-2 rounded-xl border-2 font-bold text-sm transition-all duration-300 touch-manipulation focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#3A86FF]/40 ${
          sw.running
            ? 'border-orange-400 bg-orange-50 text-orange-700'
            : 'border-black/10 bg-white text-gray-700 hover:border-[#3A86FF]/50 hover:text-gray-900'
        }`}
        aria-label={sw.running ? t('button.pause', { defaultValue: 'Pause' }) : t('button.start', { defaultValue: 'Start' })}
      >
        <Clock className="w-4 h-4" />
        <span>{sw.running ? t('button.pause', { defaultValue: 'Pause' }) : t('button.start', { defaultValue: 'Start' })}</span>
        <span className="font-mono tabular-nums">{sw.fmt(sw.elapsed)}</span>
      </button>
      {sw.elapsed > 0 && (
        <button
          onClick={sw.reset}
          className="min-h-[48px] inline-flex items-center justify-center px-3 py-2 rounded-xl border-2 border-black/10 text-gray-500 hover:text-gray-700 hover:border-gray-400 touch-manipulation transition-all duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#3A86FF]/40"
          aria-label={t('button.reset', { defaultValue: 'Reset' })}
        >
          {t('button.reset', { defaultValue: 'Reset' })}
        </button>
      )}
    </div>
  );
}

function normalizeTrainingSections(rawData) {
  if (Array.isArray(rawData)) return rawData;
  if (Array.isArray(rawData?.sections)) return rawData.sections;
  return [];
}

export default function SchreibenTrainingPage({
  examData,
  subscriptionStatus,
  isLoadingSubscription,
  onBack,
}) {
  const { t } = useTranslation();
  const navigate = useLocalizedNavigate();
  const sw = useStopwatch();

  const provider = String(examData?.examProvider || 'telc').toLowerCase().trim();
  const level = String(examData?.level || 'b1').toLowerCase().trim();

  // In-progress drafts (written text) survive refresh / tab close.
  const trainingSessionKey = `logami:v1:training:writing:${auth.currentUser?.uid || 'anon'}:${provider}-${level}`;
  const restoredTraining = useMemo(() => readSession(trainingSessionKey), [trainingSessionKey]);

  const [samples, setSamples] = useState([]);
  const [sampleIdx, setSampleIdx] = useState(0);
  const [sampleData, setSampleData] = useState(null);
  const [selectedPart, setSelectedPart] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showInstructions, setShowInstructions] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [historyPartSuffix, setHistoryPartSuffix] = useState(null); // "-{sectionIndex}-{partIndex}" or null = all
  const [pendingPart, setPendingPart] = useState(null);             // Teil chosen, sample not yet picked
  const [showPremiumModal, setShowPremiumModal] = useState(false);
  const [blockedProbeNumber, setBlockedProbeNumber] = useState(null);
  const [pendingExitNav, setPendingExitNav] = useState(null);

  const handleAhaDismiss = () => {
    setShowPremiumModal(false);
    if (typeof pendingExitNav === 'function') pendingExitNav();
    setPendingExitNav(null);
  };
  const handleAhaUpgrade = () => { setShowPremiumModal(false); setPendingExitNav(null); };
  const [resolvedSubscriptionStatus, setResolvedSubscriptionStatus] = useState(subscriptionStatus ?? null);
  const [isResolvingSubscription, setIsResolvingSubscription] = useState(false);
  const [allAnswers, setAllAnswers] = useState(() => restoredTraining?.allAnswers ?? {});

  useEffect(() => {
    writeSession(trainingSessionKey, { allAnswers });
  }, [trainingSessionKey, allAnswers]);

  const [redemittelCards, setRedemittelCards] = useState([]);
  const [isLoadingRedemittel, setIsLoadingRedemittel] = useState(false);
  // AI correction state: keyed by partKey
  const [correctionsByPartKey, setCorrectionsByPartKey] = useState({});
  const correctionUnsubsRef = useRef({});
  // Free-plan one-time usage flags (null while loading)
  const [freeAttempts, setFreeAttempts] = useState(null);

  useEffect(() => {
    const uid = auth.currentUser?.uid;
    if (!uid) return;
    freeAttemptService.getFreeAttempts(uid).then(setFreeAttempts);
  }, []);

  // Load persisted corrections from Firestore on mount
  useEffect(() => {
    const uid = auth.currentUser?.uid;
    if (!uid || !provider || !level) return;
    enhancedUserService.loadPracticeWritingHistory(uid, provider, level).then((history) => {
      if (!history.length) return;
      setCorrectionsByPartKey((prev) => {
        const next = { ...prev };
        history.forEach((entry) => {
          // Only populate if not already in state (in-session correction takes priority)
          if (!next[entry.partKey]) {
            const isStillPending = !entry.feedback && entry.status !== 'failed' && !!entry.submissionId;
            next[entry.partKey] = {
              // Pending entries resume the "correcting" state; a listener is re-attached below.
              isCorrecting: isStillPending,
              error: entry.status === 'failed' ? 'failed' : null,
              feedback: entry.feedback || null,
              writingData: entry.writingData || null,
              submissionId: entry.submissionId,
              sampleId: entry.sampleId,
              taskType: entry.taskType,
              savedAt: entry.savedAt,
            };
          }
        });
        return next;
      });
    });
  }, [provider, level]);

  // Keep local status aligned with parent prop while still allowing a direct refresh in this page.
  useEffect(() => {
    if (subscriptionStatus) {
      setResolvedSubscriptionStatus(subscriptionStatus);
    }
  }, [subscriptionStatus]);

  useEffect(() => {
    let cancelled = false;
    const uid = auth.currentUser?.uid;

    if (!uid) {
      return () => {
        cancelled = true;
      };
    }

    const refreshSubscriptionStatus = async () => {
      try {
        setIsResolvingSubscription(true);
        const latestStatus = await subscriptionService.getSubscriptionStatus(uid, { disableSyncWrite: true });
        if (!cancelled) {
          setResolvedSubscriptionStatus(latestStatus);
        }
      } catch (error) {
        console.error('Error refreshing Schreiben subscription status:', error);
        if (!cancelled) {
          setResolvedSubscriptionStatus(prev => prev || subscriptionStatus || subscriptionService.getDefaultFreeStatus());
        }
      } finally {
        if (!cancelled) {
          setIsResolvingSubscription(false);
        }
      }
    };

    refreshSubscriptionStatus();

    const handleFocus = () => {
      refreshSubscriptionStatus();
    };

    if (typeof window !== 'undefined') {
      window.addEventListener('focus', handleFocus);
    }

    return () => {
      cancelled = true;
      if (typeof window !== 'undefined') {
        window.removeEventListener('focus', handleFocus);
      }
    };
  }, [subscriptionStatus]);

  const effectiveSubscriptionStatus = resolvedSubscriptionStatus || subscriptionStatus;
  const isSubscriptionLoading = isLoadingSubscription || isResolvingSubscription;

  const isAdmin = adminService.isAdmin();
  const hasPremium = isAdmin || (!isSubscriptionLoading && effectiveSubscriptionStatus && !effectiveSubscriptionStatus.isFree && effectiveSubscriptionStatus.isActive);
  const isFreeModeLockedToFirstProbe = !isSubscriptionLoading && !hasPremium;

  useEffect(() => {
    if (!isFreeModeLockedToFirstProbe) return;

    if (sampleIdx > 0) {
      setSampleIdx(0);
      setSelectedPart(null);
      setShowInstructions(false);
    }
  }, [isFreeModeLockedToFirstProbe, sampleIdx]);

  useEffect(() => {
    let cancelled = false;

    async function loadIndex() {
      setLoading(true);
      setError(null);
      setSamples([]);
      setSampleIdx(0);
      setSampleData(null);
      setSelectedPart(null);
      setShowInstructions(false);

      try {
        const response = await fetch('/data/exam-training/schreiben/index.json');
        if (!response.ok) throw new Error('index');

        const indexData = await response.json();
        const manifestSamples = indexData?.[provider]?.[level] || [];

        if (cancelled) return;
        setSamples(manifestSamples);

        if (!manifestSamples.length) {
          setError(t('schreiben.errorNoData', { defaultValue: 'No writing training files available for this exam level yet.' }));
        }
      } catch {
        if (cancelled) return;
        setError(t('schreiben.errorNoData', { defaultValue: 'No writing training files available for this exam level yet.' }));
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    loadIndex();
    return () => {
      cancelled = true;
    };
  }, [provider, level, t]);

  useEffect(() => {
    if (!samples.length) {
      setSampleData(null);
      return;
    }

    let cancelled = false;

    async function loadSampleData() {
      setLoading(true);
      setError(null);
      setSampleData(null);
      setShowInstructions(false);

      try {
        const selectedSample = samples[sampleIdx];
        if (!selectedSample) throw new Error('sample');

        const response = await fetch(`/data/exam-training/schreiben/${provider}/${level}/${selectedSample.id}.json`);
        if (!response.ok) throw new Error('sample-file');

        const data = await response.json();
        const normalized = normalizeTrainingSections(data);

        if (cancelled) return;

        if (!normalized.length) {
          setError(t('schreiben.errorLoadFailed', { defaultValue: 'The selected writing file could not be loaded.' }));
          setSampleData(null);
        } else {
          setSampleData(normalized);
        }
      } catch {
        if (cancelled) return;
        setError(t('schreiben.errorLoadFailed', { defaultValue: 'The selected writing file could not be loaded.' }));
        setSampleData(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    loadSampleData();

    return () => {
      cancelled = true;
    };
  }, [samples, sampleIdx, provider, level, t]);

  useEffect(() => {
    let cancelled = false;

    async function loadWritingRedemittel() {
      setIsLoadingRedemittel(true);
      try {
        const response = await fetch(`/data/redemittel/${provider}-${level}-redemittel.json`);
        if (!response.ok) throw new Error('redemittel');
        const data = await response.json();
        if (cancelled) return;
        setRedemittelCards(flattenWritingRedemittelCards(data, { provider, level }));
      } catch {
        if (!cancelled) setRedemittelCards([]);
      } finally {
        if (!cancelled) setIsLoadingRedemittel(false);
      }
    }

    loadWritingRedemittel();
    return () => {
      cancelled = true;
    };
  }, [provider, level]);

  const handleBack = useCallback(() => {
    if (selectedPart !== null) {
      setSelectedPart(null);
      setShowInstructions(false);
      sw.reset();
      return;
    }
    if (pendingPart !== null) {
      setPendingPart(null);
      return;
    }
    const dest = onBack ? () => onBack() : () => navigate('/practice');
    if (isFreeModeLockedToFirstProbe) {
      setPendingExitNav(() => dest);
      setShowPremiumModal(true);
      trackPaywallShown({ context: 'schreiben' });
      return;
    }
    dest();
  }, [selectedPart, pendingPart, onBack, navigate, sw, isFreeModeLockedToFirstProbe]);

  const selectSampleForPendingPart = useCallback(
    (i) => {
      if (i < 0 || i >= samples.length || pendingPart === null) return;

      if (isFreeModeLockedToFirstProbe && i > 0) {
        setBlockedProbeNumber(i + 1);
        setShowPremiumModal(true);
        trackPaywallShown({ context: 'schreiben' });
        return;
      }

      setSampleIdx(i);
      setSelectedPart(pendingPart);
      setShowInstructions(false);
      sw.reset();
    },
    [samples.length, pendingPart, isFreeModeLockedToFirstProbe, sw]
  );

  const openPart = useCallback(
    (sectionIndex, partIndex) => {
      // Sample (Muster) is picked on the next screen; the paywall check for
      // locked samples happens there in selectSampleForPendingPart.
      setPendingPart({ sectionIndex, partIndex });
    },
    []
  );

  const currentSection = selectedPart !== null ? sampleData?.[selectedPart.sectionIndex] : null;
  const currentPart = selectedPart !== null ? currentSection?.parts?.[selectedPart.partIndex] : null;
  const partKey = selectedPart !== null
    ? `${sampleIdx}-${selectedPart.sectionIndex}-${selectedPart.partIndex}`
    : null;

  const currentPartDraft = useMemo(() => {
    if (!partKey) return null;
    return allAnswers[partKey] || getInitialPartDraft(currentPart);
  }, [partKey, allAnswers, currentPart]);

  const hasTaskChoice = !!(currentPart?.optional && Array.isArray(currentPart?.tasks) && currentPart.tasks.length > 1);
  const selectedTaskIndex = hasTaskChoice
    ? (typeof currentPartDraft?.selectedTaskIndex === 'number' ? currentPartDraft.selectedTaskIndex : null)
    : 0;

  const activeTask = useMemo(() => {
    if (!currentPart) return null;

    if (hasTaskChoice) {
      if (selectedTaskIndex === null) return null;
      return currentPart.tasks?.[selectedTaskIndex] || null;
    }

    return currentPart.task || currentPart.tasks?.[0] || null;
  }, [currentPart, hasTaskChoice, selectedTaskIndex]);

  const effectiveTaskType = useMemo(
    () => inferTaskType(activeTask?.taskType || currentPart?.taskType, activeTask),
    [activeTask, currentPart?.taskType]
  );

  const activeFieldValues = useMemo(() => {
    if (!currentPartDraft) return {};
    if (hasTaskChoice && selectedTaskIndex !== null) {
      return currentPartDraft.taskFields?.[selectedTaskIndex] || {};
    }
    return currentPartDraft.fields || {};
  }, [currentPartDraft, hasTaskChoice, selectedTaskIndex]);

  const writingStructure = useMemo(
    () => buildWritingStructure(effectiveTaskType, activeTask, t, { provider, level }),
    [effectiveTaskType, activeTask, t, provider, level]
  );

  const composedDraftText = useMemo(
    () => composeStructuredText(writingStructure, activeFieldValues),
    [writingStructure, activeFieldValues]
  );

  const [previewManualEdit, setPreviewManualEdit] = useState(null);

  // Reset manual override whenever the user switches part or task
  useEffect(() => {
    setPreviewManualEdit(null);
  }, [partKey, selectedTaskIndex]);

  const effectiveDraftText = previewManualEdit ?? composedDraftText;

  const composedDraftWordCount = useMemo(
    () => countWords(composedDraftText),
    [composedDraftText]
  );

  const effectiveDraftWordCount = useMemo(
    () => countWords(effectiveDraftText),
    [effectiveDraftText]
  );

  const wordGoalLabel = useMemo(
    () => buildWordGoalLabel(activeTask?.wordLimit || currentPart?.wordLimit, t),
    [activeTask?.wordLimit, currentPart?.wordLimit, t]
  );

  const updateCurrentPartDraft = useCallback((updater) => {
    if (!partKey) return;

    setAllAnswers((prev) => {
      const baseDraft = prev[partKey] || getInitialPartDraft(currentPart);
      const nextDraft = typeof updater === 'function' ? updater(baseDraft) : { ...baseDraft, ...updater };
      return {
        ...prev,
        [partKey]: nextDraft,
      };
    });
  }, [partKey, currentPart]);

  const selectOptionalTask = useCallback((taskIndex) => {
    updateCurrentPartDraft((draft) => ({
      ...draft,
      selectedTaskIndex: taskIndex,
      openHints: {},
    }));
    sw.reset();
  }, [updateCurrentPartDraft, sw]);

  const handleStructureFieldChange = useCallback((fieldId, value) => {
    updateCurrentPartDraft((draft) => {
      if (hasTaskChoice && selectedTaskIndex !== null) {
        const currentTaskFields = draft.taskFields?.[selectedTaskIndex] || {};
        return {
          ...draft,
          taskFields: {
            ...(draft.taskFields || {}),
            [selectedTaskIndex]: {
              ...currentTaskFields,
              [fieldId]: value,
            },
          },
        };
      }

      return {
        ...draft,
        fields: {
          ...(draft.fields || {}),
          [fieldId]: value,
        },
      };
    });
  }, [updateCurrentPartDraft, hasTaskChoice, selectedTaskIndex]);

  const toggleHintPanel = useCallback((fieldId) => {
    updateCurrentPartDraft((draft) => ({
      ...draft,
      openHints: {
        ...(draft.openHints || {}),
        [fieldId]: !draft.openHints?.[fieldId],
      },
    }));
  }, [updateCurrentPartDraft]);

  const jumpBackToTaskChooser = useCallback(() => {
    updateCurrentPartDraft((draft) => ({
      ...draft,
      selectedTaskIndex: null,
    }));
  }, [updateCurrentPartDraft]);

  // ── AI Correction ──────────────────────────────────────────────────────────
  // Unsubscribe all Firestore listeners on unmount.
  useEffect(() => {
    const unsubs = correctionUnsubsRef.current;
    return () => {
      Object.values(unsubs).forEach((fn) => { try { fn(); } catch (_) {} });
    };
  }, []);

  // Listen to a writingSubmissions doc until it is corrected (or fails) and keep
  // both the UI state and the persisted practiceWritingHistory entry in sync.
  // Used for fresh submissions and for pending corrections restored after a reload.
  const attachCorrectionListener = useCallback((pKey, submissionId, writingDataForSave, sampleId, taskType) => {
    if (correctionUnsubsRef.current[pKey]) {
      correctionUnsubsRef.current[pKey]();
    }
    const submissionRef = doc(db, 'writingSubmissions', submissionId);
    const unsubscribe = onSnapshot(submissionRef, (snap) => {
      if (!snap.exists()) return;
      const data = snap.data();
      if (data.status === 'corrected') {
        setCorrectionsByPartKey((prev) => ({
          ...prev,
          [pKey]: { isCorrecting: false, error: null, feedback: data, writingData: writingDataForSave, submissionId, sampleId, taskType },
        }));
        // Persist to user's history so it loads next session
        const uid = auth.currentUser?.uid;
        if (uid) {
          enhancedUserService.savePracticeWritingHistory(uid, {
            submissionId,
            provider,
            level,
            sampleId: sampleId || `${provider}-${level}-schreiben`,
            partKey: pKey,
            taskType,
            status: 'corrected',
            feedback: data,
            writingData: writingDataForSave,
          });
        }
        // Stop listening once corrected
        unsubscribe();
        delete correctionUnsubsRef.current[pKey];
      } else if (data.status === 'failed' || String(data.status || '').startsWith('error_')) {
        const isFreeLimitReached = data.errorMessage === 'free-limit-reached';
        if (isFreeLimitReached) {
          setFreeAttempts(prev => ({ ...(prev || {}), writingCorrection: true }));
          setShowPremiumModal(true);
        } else {
          // Correction failed for another reason, the server refunds the free
          // attempt, so roll back the optimistic flag to allow a retry.
          setFreeAttempts(prev => {
            if (!prev?.writingCorrection) return prev;
            const next = { ...prev };
            delete next.writingCorrection;
            return next;
          });
        }
        setCorrectionsByPartKey((prev) => ({
          ...prev,
          [pKey]: { isCorrecting: false, error: 'failed', feedback: null, writingData: null, submissionId },
        }));
        const uid = auth.currentUser?.uid;
        if (uid) {
          enhancedUserService.savePracticeWritingHistory(uid, {
            submissionId, provider, level, partKey: pKey, taskType, status: 'failed',
          });
        }
        unsubscribe();
        delete correctionUnsubsRef.current[pKey];
      }
    });
    correctionUnsubsRef.current[pKey] = unsubscribe;
  }, [provider, level]);

  // Attach listeners for any pending correction that has none yet (fresh
  // submission or an entry restored from history after a reload).
  useEffect(() => {
    Object.entries(correctionsByPartKey).forEach(([pKey, correction]) => {
      if (!correction?.isCorrecting || !correction?.submissionId || correction?.feedback) return;
      if (correctionUnsubsRef.current[pKey]) return;
      attachCorrectionListener(pKey, correction.submissionId, correction.writingData || null, correction.sampleId, correction.taskType);
    });
  }, [correctionsByPartKey, attachCorrectionListener]);

  const submitForCorrection = useCallback(async () => {
    const userId = auth.currentUser?.uid;
    if (!userId || !partKey || !effectiveDraftText) return;

    // Pessimistic word-count guard (prevent empty submissions)
    if (effectiveDraftWordCount < 20) return;

    // Free plan: exactly ONE AI correction.
    if (isFreeModeLockedToFirstProbe && freeAttemptService.isWritingCorrectionUsed(freeAttempts)) {
      setShowPremiumModal(true);
      return;
    }

    // Mark as correcting
    setCorrectionsByPartKey((prev) => ({
      ...prev,
      [partKey]: { isCorrecting: true, error: null, feedback: null, writingData: null, submissionId: null },
    }));

    try {
      const submissionData = {
        examProvider: provider,
        examLevel: level,
        source: 'practice',
        sampleId: samples[sampleIdx]?.id || `${provider}-${level}-schreiben`,
        sampleIdx,
        partId: currentPart?.partId || partKey,
        partName: PART_TYPE_LABELS[effectiveTaskType] || effectiveTaskType,
        hasMultipleTexts: false,
        userText: effectiveDraftText,
        taskType: effectiveTaskType,
        texts: {
          main: {
            text: effectiveDraftText,
            wordCount: effectiveDraftWordCount,
            taskType: effectiveTaskType,
            partName: PART_TYPE_LABELS[effectiveTaskType] || effectiveTaskType,
            taskData: {
              scenario: activeTask?.scenario || '',
              inputContent: activeTask?.inputContent || '',
              guidingPoints: Array.isArray(activeTask?.guidingPoints) ? activeTask.guidingPoints : [],
            },
          },
        },
      };

      const submissionId = await enhancedUserService.saveWritingSubmission(userId, submissionData);
      if (!submissionId) throw new Error('save-failed');

      // Free plan: this submission uses up the single free AI correction.
      // Only update local UI state here, the flag doc is created SERVER-SIDE by
      // the correctwritingsubmission function (a client write would race the
      // function's check and block the user's own first submission), and the
      // server refunds it if the correction fails.
      if (isFreeModeLockedToFirstProbe) {
        setFreeAttempts(prev => ({ ...(prev || {}), writingCorrection: true }));
      }

      // Persist a pending history entry immediately so the correction is never
      // lost if the user leaves before the result arrives (self-healed on next
      // load by loadPracticeWritingHistory).
      const sampleId = samples[sampleIdx]?.id || `${provider}-${level}-schreiben`;
      const pendingWritingData = {
        hasMultipleTexts: false,
        texts: { main: { text: effectiveDraftText, taskType: effectiveTaskType, partName: PART_TYPE_LABELS[effectiveTaskType] || effectiveTaskType } },
      };
      enhancedUserService.savePracticeWritingHistory(userId, {
        submissionId,
        provider,
        level,
        sampleId,
        partKey,
        taskType: effectiveTaskType,
        status: 'pending',
        writingData: pendingWritingData,
      });

      // The pending-correction effect attaches the snapshot listener for this entry.
      setCorrectionsByPartKey((prev) => ({
        ...prev,
        [partKey]: { ...prev[partKey], submissionId, writingData: pendingWritingData, sampleId, taskType: effectiveTaskType },
      }));
    } catch (err) {
      console.error('[SchreibenTraining] submitForCorrection error', err);
      setCorrectionsByPartKey((prev) => ({
        ...prev,
        [partKey]: { isCorrecting: false, error: 'failed', feedback: null, writingData: null, submissionId: null },
      }));
    }
  }, [partKey, effectiveDraftText, effectiveDraftWordCount, provider, level, samples, sampleIdx, currentPart, effectiveTaskType, activeTask, isFreeModeLockedToFirstProbe, freeAttempts]);

  const writingSections = (sampleData || []).filter((section) => Array.isArray(section?.parts) && section.parts.length > 0);
  const activeProbeLabel = String(samples?.[sampleIdx]?.label || '').replace(
    /^Probe\b/i,
    t('schreiben.probeLabel', { defaultValue: 'Test' })
  );

  const renderPartCard = (section, sectionIndex, part, partIndex) => {
    const partTypeLabel = PART_TYPE_LABELS[part.taskType] || part.taskType;
    const partDisplayNumber = part.partNumber || partIndex + 1;
    const PartIcon = PART_ICON_COMPONENTS[part.taskType] || PenLine;
    const hasChoice = !!(part?.optional && Array.isArray(part?.tasks) && part.tasks.length > 1);
    const previewWordLimit = part?.task?.wordLimit || part?.tasks?.[0]?.wordLimit || part?.wordLimit;
    const previewWordGoal = buildWordGoalLabel(previewWordLimit, t);
    const cardPartKey = `${sampleIdx}-${sampleData?.indexOf(section)}-${partIndex}`;
    const cardCorrection = correctionsByPartKey[cardPartKey];
    const hasCorrectionResult = !!cardCorrection?.feedback;
    const isCardCorrecting = !!cardCorrection?.isCorrecting;

    return (
      <button
        key={part.partId || `${section.sectionId || 'writing'}-${partIndex}`}
        onClick={() => openPart(sampleData?.indexOf(section), partIndex)}
        className="group relative overflow-hidden rounded-2xl border-2 border-black/10 bg-white p-4 text-left hover:border-[#3A86FF]/38 hover:shadow-[0_18px_34px_rgba(58,134,255,0.14)] hover:-translate-y-1 active:scale-[0.995] transition-all duration-300 touch-manipulation"
      >
        <div className="absolute inset-0 pointer-events-none bg-gradient-to-br from-[#3A86FF]/10 via-white to-[#0F172A]/[0.03] opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

        <div className="relative z-10 flex items-start gap-3.5">
          <div className="inline-flex items-center justify-center w-11 h-11 rounded-xl bg-[#3A86FF]/10 text-[#3A86FF]">
            <PartIcon className="w-5 h-5" />
          </div>

          <div className="min-w-0 flex-1">
            <p className="font-black text-gray-900 text-sm sm:text-base leading-tight">
              {t('schreiben.partLabel', { defaultValue: 'Teil' })} {partDisplayNumber}
            </p>
            <p className="text-xs text-gray-500 mt-1">{partTypeLabel}</p>

            <div className="mt-3 flex flex-wrap items-center gap-2 text-xs">
              <span className="inline-flex items-center rounded-full border border-black/10 bg-gray-50 px-2.5 py-1 font-semibold text-gray-600">
                {part.points} {t('schreiben.points')}
              </span>
              {previewWordGoal && (
                <span className="inline-flex items-center rounded-full border border-[#3A86FF]/20 bg-[#3A86FF]/8 px-2.5 py-1 font-semibold text-[#275eb4]">
                  {previewWordGoal}
                </span>
              )}
              {hasChoice && (
                <span className="inline-flex items-center rounded-full border border-black/10 bg-white px-2.5 py-1 font-semibold text-gray-600">
                  {t('schreiben.chooseTopic', { defaultValue: 'Choose one topic first' })}
                </span>
              )}
            </div>

            <div className="mt-3 inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-black text-white bg-gradient-to-r from-black via-[#0F172A] to-[#1F3B63] shadow-[0_10px_22px_rgba(15,23,42,0.2)]">
              {t('schreiben.startPart', { defaultValue: 'Start training' })}
              <ChevronRight className="h-3.5 w-3.5" />
            </div>

            {isCardCorrecting && (
              <div className="mt-2 inline-flex items-center gap-1.5 rounded-full border border-purple-200 bg-purple-50 px-2.5 py-1 text-[11px] font-semibold text-purple-700">
                <LogamiLogoSpinner size="xs" decorative={false} alt="Logami loading" />
                {t('schreiben.correctionPending', { defaultValue: 'Correction pending…' })}
              </div>
            )}
            {hasCorrectionResult && !isCardCorrecting && (
              <div className="mt-2 inline-flex items-center gap-1.5 rounded-full border border-green-200 bg-green-50 px-2.5 py-1 text-[11px] font-semibold text-green-700">
                <CheckCircle className="w-3 h-3" />
                {t('schreiben.correctionAvailable', { defaultValue: 'Correction available' })}
              </div>
            )}
          </div>
        </div>
      </button>
    );
  };

  let content = null;

  if (loading) {
    content = (
      <div className="min-h-[50vh] flex items-center justify-center px-4">
        <div className={`${ELEVATED_SURFACE} p-8 w-full max-w-sm text-center animate-fade-in-up`}>
          <LogamiLogoSpinner size="xl" className="mx-auto" decorative={false} alt="Logami loading" />
          <p className="mt-4 text-sm text-gray-600 font-semibold">
            {t('schreiben.loading', { defaultValue: 'Loading writing training data...' })}
          </p>
        </div>
      </div>
    );
  } else if (error || !samples.length) {
    content = (
      <div className="min-h-[50vh] flex items-center justify-center px-4">
        <div className={`${ELEVATED_SURFACE} text-center max-w-sm w-full p-6 animate-fade-in-up`}>
          <AlertCircle className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-600 font-semibold mb-2">{error}</p>
          <button
            onClick={handleBack}
            className="min-h-[48px] inline-flex items-center justify-center px-4 py-2 rounded-xl border-2 border-black bg-black text-white font-bold text-sm hover:bg-gray-900 touch-manipulation transition-all duration-300"
          >
            {t('grammar.back', { defaultValue: 'Back' })}
          </button>
        </div>
      </div>
    );
  } else if (showHistory) {
    content = (
      <PracticeHistoryPanel
        mode="writing"
        provider={provider}
        level={level}
        onClose={() => setShowHistory(false)}
        filterAttempt={historyPartSuffix ? (attempt) => typeof attempt.partKey === 'string' && attempt.partKey.endsWith(historyPartSuffix) : undefined}
      />
    );
  } else if (pendingPart !== null && selectedPart === null) {
    const pendingPartData = sampleData?.[pendingPart.sectionIndex]?.parts?.[pendingPart.partIndex];
    content = (
      <SampleSelectView
        partName={pendingPartData?.partName || PART_TYPE_LABELS[pendingPartData?.taskType] || `Teil ${pendingPart.partIndex + 1}`}
        partTypeLabel={PART_TYPE_LABELS[pendingPartData?.taskType] || pendingPartData?.taskType}
        samples={samples}
        formatLabel={(label) => String(label || '').replace(/^Probe\b/i, t('schreiben.probeLabel', { defaultValue: 'Test' }))}
        isSampleLocked={(i) => isFreeModeLockedToFirstProbe && i > 0}
        getSessionProgress={(i) => {
          const draft = allAnswers[`${i}-${pendingPart.sectionIndex}-${pendingPart.partIndex}`];
          if (!draft) return null;
          const texts = [
            ...Object.values(draft.fields || {}),
            ...Object.values(draft.taskFields || {}).flatMap(tf => Object.values(tf || {})),
          ];
          return texts.some(v => typeof v === 'string' && v.trim().length > 0) ? true : null;
        }}
        onSelectSample={selectSampleForPendingPart}
        onBack={() => setPendingPart(null)}
        onOpenHistory={() => { setHistoryPartSuffix(`-${pendingPart.sectionIndex}-${pendingPart.partIndex}`); setShowHistory(true); }}
      />
    );
  } else if (selectedPart === null) {
    content = (
      <div className="animate-fade-in-up">
        <div className={`${ELEVATED_SURFACE} p-3.5 sm:p-[18px] mb-5 sm:mb-6`}>
          <div className="grid grid-cols-[auto_1fr_auto] items-start sm:items-center gap-3 sm:gap-4">
            <div className="justify-self-start hidden sm:block">
              <AnimatedBackButton onClick={handleBack} backLabel={t('common.goBack')} />
            </div>

            <div className="text-center">
              <div className="inline-flex items-center gap-2 px-3 py-1 bg-black text-white text-[11px] font-bold tracking-widest uppercase rounded-full mb-1">
                <PenLine className="w-3.5 h-3.5" />
                <span>{t('schreiben.title')}</span>
              </div>
              <h1 className="font-space text-2xl sm:text-4xl font-black text-gray-900 leading-tight">
                {(examData?.examProvider || 'TELC').toUpperCase()} {(examData?.level || 'B1').toUpperCase()}
              </h1>
              <p className="text-xs sm:text-sm text-gray-500 mt-1">{t('schreiben.subtitle')}</p>
            </div>

            <div className="hidden sm:block" />
          </div>

        </div>

        {writingSections[0]?.instructions && (
          <div className={`${ELEVATED_SURFACE} p-3.5 sm:p-4 mb-5 sm:mb-6`}>
            <button
              onClick={() => setShowInstructions((open) => !open)}
              className="w-full min-h-[44px] flex items-center justify-between gap-2 text-left touch-manipulation"
              aria-expanded={showInstructions}
            >
              <div className="flex items-center gap-2">
                <BookOpen className="w-4 h-4 text-[#3A86FF] flex-shrink-0" />
                <span className="font-bold text-sm text-gray-900">{t('schreiben.instructions')}</span>
              </div>
              <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${showInstructions ? 'rotate-180' : ''}`} />
            </button>

            {showInstructions && (
              <div className="mt-3 pt-3 border-t border-black/10 prose prose-sm max-w-none text-gray-700 animate-fade-in">
                <div className="inline-flex items-center gap-1.5 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2.5 py-1 mb-3">
                  <Lightbulb className="w-3.5 h-3.5" />
                  <span>{t('schreiben.instructions')}</span>
                </div>
                <div dangerouslySetInnerHTML={{ __html: marked(writingSections[0].instructions) }} />
              </div>
            )}
          </div>
        )}

        <div className="space-y-5">
          {writingSections.map((section, sectionLocalIdx) => {
            const sectionIndex = sampleData.indexOf(section);

            return (
              <div key={`${section.sectionId || 'writing'}-${sectionLocalIdx}`} className={`${ELEVATED_SURFACE} relative p-3.5 sm:p-4 animate-fade-in-up`}>
                <div className="absolute inset-x-0 top-0 h-[3px] rounded-t-2xl bg-gradient-to-r from-transparent via-[#3A86FF]/35 to-transparent" />

                <div className="relative z-10 flex items-center gap-3 mb-3.5">
                  <div className="h-px flex-1 bg-gradient-to-r from-transparent via-[#3A86FF]/35 to-transparent" />
                  <span className="text-[11px] font-black uppercase tracking-widest text-gray-500 px-2">
                    {section.sectionName || t('schreiben.title')}
                  </span>
                  <div className="h-px flex-1 bg-gradient-to-r from-transparent via-[#3A86FF]/35 to-transparent" />
                </div>

                <div className="relative z-10 space-y-2.5">
                  {(section.parts || []).map((part, partIndex) => {
                    const cardPartKey = `${sampleIdx}-${sectionIndex}-${partIndex}`;
                    const correction = correctionsByPartKey[cardPartKey];
                    return (
                      <React.Fragment key={part.partId || `${section.sectionId || 'writing'}-${partIndex}`}>
                        {renderPartCard(section, sectionIndex, part, partIndex)}
                        {correction?.feedback && correction?.writingData && (
                          <div className="rounded-2xl border border-purple-200 bg-gradient-to-br from-purple-50/70 to-pink-50/70 p-4 animate-fade-in-up">
                            <div className="flex items-center gap-2 mb-3">
                              <Sparkles className="w-4 h-4 text-purple-600 flex-shrink-0" />
                              <span className="font-black text-purple-900 text-sm">
                                {t('schreiben.aiCorrectionResult', { defaultValue: "Logami's AI Correction" })}
                              </span>
                              <span className="ml-auto inline-flex items-center rounded-full border border-purple-200 bg-white px-2 py-0.5 text-[11px] font-semibold text-purple-700">
                                {PART_TYPE_LABELS[part.taskType] || part.taskType}
                              </span>
                            </div>
                            <PracticeWritingFeedback
                              feedback={correction.feedback}
                              writingData={correction.writingData}
                            />
                            <AskTeacherPanel submissionId={correction.submissionId} />
                          </div>
                        )}
                        {correction?.isCorrecting && (
                          <div className="flex items-center gap-3 rounded-xl border border-purple-100 bg-purple-50 p-3 animate-fade-in">
                            <LogamiLogoSpinner size="md" decorative={false} alt="Logami loading" className="flex-shrink-0" />
                            <p className="text-sm font-semibold text-purple-700">
                              {t('schreiben.correctionInProgress', { defaultValue: "Logami's AI is analysing your text…" })}
                            </p>
                          </div>
                        )}
                      </React.Fragment>
                    );
                  })}
                </div>

                <div className="relative z-10 mt-3.5 flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                  <span className="inline-block h-1.5 w-1.5 rounded-full bg-[#3A86FF]" />
                  {section.parts?.length || 0} {t('schreiben.partsLabel', { defaultValue: 'parts' })}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  } else {
    const partName = PART_TYPE_LABELS[effectiveTaskType] || effectiveTaskType || t('schreiben.title');
    const selectedTaskLabel = hasTaskChoice && selectedTaskIndex !== null
      ? currentPart?.tasks?.[selectedTaskIndex]?.id
      : null;
    const openHints = currentPartDraft?.openHints || {};
    const guidingPoints =
      Array.isArray(activeTask?.guidingPoints) && activeTask.guidingPoints.length > 0
        ? activeTask.guidingPoints
        : extractScenarioBulletPoints(activeTask?.scenario || '');

    content = (
      <div className="animate-fade-in-up">
        <div className={`${ELEVATED_SURFACE} p-3.5 sm:p-[18px] mb-5 sm:mb-6`}>
          <div className="grid grid-cols-[auto_1fr_auto] items-start sm:items-center gap-3 sm:gap-4">
            <div className="justify-self-start hidden sm:block">
              <AnimatedBackButton onClick={handleBack} backLabel={t('common.goBack')} />
            </div>

            <div className="text-center">
              <div className="inline-flex items-center gap-2 px-3 py-1 bg-black text-white text-[11px] font-bold tracking-widest uppercase rounded-full mb-1">
                <PenLine className="w-3.5 h-3.5" />
                <span>{t('schreiben.title')}</span>
              </div>
              <h1 className="font-space text-xl sm:text-3xl font-black text-gray-900 leading-tight">{partName}</h1>
              <p className="text-xs sm:text-sm text-gray-500 mt-1">
                {activeProbeLabel} • {t('schreiben.partLabel', { defaultValue: 'Teil' })} {currentPart?.partNumber || selectedPart.partIndex + 1}
                {selectedTaskLabel ? ` • ${selectedTaskLabel}` : ''}
              </p>
            </div>

            <div className="justify-self-end">
              <Stopwatch sw={sw} t={t} />
            </div>
          </div>
        </div>

        {hasTaskChoice && selectedTaskIndex === null ? (
          <div className={`${ELEVATED_SURFACE} p-4 sm:p-5`}> 
            <div className="flex items-center gap-2 mb-3">
              <FileText className="w-4 h-4 text-[#3A86FF]" />
              <h2 className="font-black text-gray-900 text-base sm:text-lg">
                {t('schreiben.chooseTopic', { defaultValue: 'Choose one topic first' })}
              </h2>
            </div>

            <p className="text-sm text-gray-600 mb-4">
              {t('schreiben.chooseTopicHint', { defaultValue: 'This part contains multiple topic options. Select one and start writing with guided structure blocks.' })}
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
              {(currentPart?.tasks || []).map((task, taskIndex) => (
                <button
                  key={task.id || `${currentPart?.partId}-task-${taskIndex}`}
                  onClick={() => selectOptionalTask(taskIndex)}
                  className="group text-left rounded-2xl border-2 border-black/10 bg-white p-4 hover:border-[#3A86FF]/35 hover:shadow-[0_14px_28px_rgba(58,134,255,0.14)] hover:-translate-y-0.5 active:scale-[0.99] transition-all duration-300 touch-manipulation"
                >
                  <div className="inline-flex items-center justify-center rounded-lg px-2.5 py-1 border border-black/10 bg-gray-50 text-xs font-bold text-gray-800 mb-2">
                    {t('schreiben.topicLabel', { defaultValue: 'Topic' })} {task.id || String.fromCharCode(65 + taskIndex)}
                  </div>

                  {task.taskType && (
                    <div className="mb-2 inline-flex items-center rounded-full border border-[#3A86FF]/25 bg-[#3A86FF]/10 px-2.5 py-1 text-[11px] font-bold text-[#275eb4]">
                      {PART_TYPE_LABELS[task.taskType] || task.taskType}
                    </div>
                  )}

                  {task.scenario && (
                    <div className="text-sm text-gray-700 leading-relaxed prose prose-sm max-w-none line-clamp-6">
                      <div dangerouslySetInnerHTML={{ __html: marked(task.scenario) }} />
                    </div>
                  )}

                  <span className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold text-[#275eb4]">
                    <ChevronRight className="w-4 h-4" />
                    {t('schreiben.startThisTopic', { defaultValue: 'Start this topic' })}
                  </span>
                </button>
              ))}
            </div>
          </div>
        ) : (
          <>
            <div className={`${ELEVATED_SURFACE} p-4 sm:p-5 mb-5 sm:mb-6`}>
              <div className="flex flex-wrap items-center gap-2">
                <span className="inline-flex items-center rounded-full border border-black/10 bg-gray-50 px-2.5 py-1 text-xs font-semibold text-gray-600">
                  {currentPart?.points} {t('schreiben.points')}
                </span>
                {wordGoalLabel && (
                  <span className="inline-flex items-center rounded-full border border-[#3A86FF]/20 bg-[#3A86FF]/8 px-2.5 py-1 text-xs font-semibold text-[#275eb4]">
                    {wordGoalLabel}
                  </span>
                )}
                {hasTaskChoice && (
                  <button
                    onClick={jumpBackToTaskChooser}
                    className="ml-auto min-h-[40px] inline-flex items-center justify-center rounded-lg border border-black/10 px-3 py-1.5 text-xs font-semibold text-gray-700 hover:border-black/25 hover:bg-gray-50 transition-all duration-200"
                  >
                    <ChevronLeft className="w-3.5 h-3.5 mr-1" />
                    {t('schreiben.chooseAnotherTopic', { defaultValue: 'Choose another topic' })}
                  </button>
                )}
              </div>

              {currentPart?.instructions && (
                <div className="mt-4 rounded-xl border border-black/10 bg-gray-50 p-3 prose prose-sm max-w-none text-gray-700">
                  <div dangerouslySetInnerHTML={{ __html: marked(currentPart.instructions) }} />
                </div>
              )}
            </div>

            <div className={`${ELEVATED_SURFACE} p-4 sm:p-5 mb-5 sm:mb-6`}>
              <div className="flex items-center gap-2 mb-3">
                <BookOpen className="w-4 h-4 text-[#3A86FF]" />
                <h2 className="font-black text-gray-900 text-base sm:text-lg">
                  {t('schreiben.examPrompt', { defaultValue: 'Exam prompt' })}
                </h2>
              </div>

              {activeTask?.scenario && (
                <div className="prose prose-sm sm:prose-base max-w-none text-gray-800 mb-4">
                  <div dangerouslySetInnerHTML={{ __html: marked(activeTask.scenario) }} />
                </div>
              )}

              {activeTask?.inputContent && (
                <div className="rounded-xl border border-black/10 bg-gray-50 p-3 sm:p-4 prose prose-sm max-w-none text-gray-700 mb-4">
                  <div dangerouslySetInnerHTML={{ __html: marked(activeTask.inputContent) }} />
                </div>
              )}

              {guidingPoints.length > 0 && (
                <div className="rounded-xl border border-black/10 bg-white p-3 sm:p-4">
                  <p className="text-xs uppercase tracking-wide font-bold text-gray-500 mb-2">
                    {t('schreiben.guidingPoints', { defaultValue: 'Guiding points' })}
                  </p>
                  <ul className="space-y-2 text-sm text-gray-700">
                    {guidingPoints.map((point, pointIndex) => {
                      const pointText = typeof point === 'string' ? point : point?.text;
                      const pointPrompt = typeof point === 'object' ? point?.prompt : null;
                      return (
                        <li key={`guiding-${pointIndex}`} className="flex gap-2">
                          <span className="mt-[3px] h-1.5 w-1.5 rounded-full bg-[#3A86FF] flex-shrink-0" />
                          <span>
                            {pointPrompt && <span className="font-semibold">{pointPrompt} </span>}
                            {pointText}
                          </span>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}
            </div>

            <div className={`${ELEVATED_SURFACE} p-4 sm:p-5 mb-5 sm:mb-6`}>
              <div className="mb-4">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#3A86FF]/10 text-[#275eb4] text-[11px] font-bold tracking-widest uppercase">
                  <ListChecks className="w-3.5 h-3.5" />
                  <span>{t('schreiben.structureBuilder', { defaultValue: 'Structured writing builder' })}</span>
                </div>
                <h2 className="font-black text-gray-900 text-base sm:text-lg mt-2">
                  {t('schreiben.structureBuilderTitle', { defaultValue: 'Write step by step with helper labels' })}
                </h2>
                <p className="text-sm text-gray-600 mt-1">
                  {t('schreiben.structureBuilderHint', { defaultValue: 'Each block has context-aware Redemittel. Open a block helper to copy phrase ideas and keep your text structured.' })}
                </p>
              </div>

              <div className="space-y-3 sm:space-y-4">
                {writingStructure.map((field) => {
                  const normalizedFieldId = String(field?.id || '').toLowerCase();
                  const isSignatureLikeField = normalizedFieldId.includes('signature') || normalizedFieldId === 'name';
                  const fieldValue = activeFieldValues[field.id] || '';
                  const phraseMatches = isSignatureLikeField
                    ? []
                    : getMatchingRedemittel(
                        redemittelCards,
                        field,
                        activeTask,
                        effectiveTaskType,
                        { provider, level }
                      );
                  const isHintOpen = !!openHints[field.id];

                  return (
                    <section key={field.id} className={`${SOFT_SURFACE} p-3 sm:p-4`}>
                      <div className={isSignatureLikeField ? 'grid grid-cols-1 gap-3 sm:gap-4' : 'grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_minmax(250px,320px)] gap-3 sm:gap-4'}>
                        <div>
                          <label className="block text-sm font-black text-gray-900 mb-1">
                            {t(field.labelKey, { defaultValue: field.defaultLabel })}
                          </label>
                          <p className="text-xs text-gray-500 mb-2.5">
                            {t(field.hintKey, { defaultValue: field.defaultHint })}
                          </p>
                          <textarea
                            value={fieldValue}
                            onChange={(event) => handleStructureFieldChange(field.id, event.target.value)}
                            rows={field.rows || 3}
                            placeholder={t(field.placeholderKey, { defaultValue: field.defaultPlaceholder })}
                            className="w-full rounded-xl border border-black/10 bg-white px-3 py-2.5 text-sm text-gray-800 placeholder:text-gray-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#3A86FF]/35 focus-visible:border-[#3A86FF]/35 transition-all duration-200"
                          />
                        </div>

                        {!isSignatureLikeField && (
                          <aside className="rounded-xl border border-black/10 bg-white/70 p-3">
                            <button
                              onClick={() => toggleHintPanel(field.id)}
                              className="w-full min-h-[40px] inline-flex items-center justify-between gap-2 rounded-lg border border-black/10 bg-gray-50 px-3 py-1.5 text-xs font-semibold text-gray-700 hover:border-black/20 hover:bg-gray-100 transition-all duration-200"
                            >
                              <span>
                                {isHintOpen
                                  ? t('schreiben.hideRedemittel', { defaultValue: 'Hide Redemittel' })
                                  : t('schreiben.showRedemittel', { defaultValue: 'Show Redemittel' })}
                              </span>
                              <span className="inline-flex items-center rounded-full bg-white border border-black/10 px-2 py-0.5 text-[11px] font-bold text-gray-600">
                                {isLoadingRedemittel ? '...' : phraseMatches.length}
                              </span>
                            </button>

                            {isHintOpen && (
                              <div className="mt-2 space-y-2 max-h-52 overflow-y-auto pr-1">
                                {isLoadingRedemittel ? (
                                  <p className="text-xs text-gray-500">
                                    {t('schreiben.loadingRedemittel', { defaultValue: 'Loading Redemittel...' })}
                                  </p>
                                ) : (
                                  phraseMatches.length > 0 ? (
                                    phraseMatches.map((phrase) => (
                                      <div key={phrase.id} className="rounded-lg border border-black/10 bg-white p-2.5">
                                        <p className="text-sm font-semibold text-gray-900">{phrase.front}</p>
                                        {phrase.context && (
                                          <p className="text-[11px] text-[#275eb4] font-semibold mt-1">{phrase.context}</p>
                                        )}
                                        {phrase.usage && (
                                          <p className="text-[11px] text-gray-500 mt-1">{phrase.usage}</p>
                                        )}
                                      </div>
                                    ))
                                  ) : (
                                    <p className="text-xs text-gray-500">
                                      {t('schreiben.noMatchingRedemittel', { defaultValue: 'No matching Redemittel available for this block yet.' })}
                                    </p>
                                  )
                                )}
                              </div>
                            )}
                          </aside>
                        )}
                      </div>
                    </section>
                  );
                })}
              </div>
            </div>

            <div className={`${SOFT_SURFACE} p-4 sm:p-5`}>
              <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                <div className="flex items-center gap-2">
                  <h3 className="font-bold text-gray-900 text-sm sm:text-base">
                    {t('schreiben.livePreview', { defaultValue: 'Live draft preview' })}
                  </h3>
                  {previewManualEdit !== null && (
                    <button
                      type="button"
                      onClick={() => setPreviewManualEdit(null)}
                      className="inline-flex items-center gap-1 rounded-full border border-indigo-200 bg-indigo-50 px-2 py-0.5 text-xs font-medium text-indigo-600 hover:bg-indigo-100 transition-colors"
                      title={t('schreiben.resyncPreview', { defaultValue: 'Re-sync from fields' })}
                    >
                      <RotateCcw size={11} />
                      {t('schreiben.resyncPreview', { defaultValue: 'Re-sync from fields' })}
                    </button>
                  )}
                </div>
                <span className="inline-flex items-center rounded-full border border-black/10 bg-white px-2.5 py-1 text-xs font-semibold text-gray-600">
                  {t('schreiben.wordCountLabel', { defaultValue: 'Word count' })}: {effectiveDraftWordCount}
                </span>
              </div>

              <textarea
                value={effectiveDraftText}
                onChange={(e) => setPreviewManualEdit(e.target.value)}
                placeholder={previewManualEdit === null ? t('schreiben.emptyDraftHint', { defaultValue: 'Start filling the blocks above to build your draft.' }) : ''}
                className="w-full whitespace-pre-wrap break-words rounded-xl border border-black/10 bg-white p-3 text-sm text-gray-700 leading-relaxed font-sans min-h-[160px] resize-y focus:outline-none focus:ring-2 focus:ring-indigo-400/50 focus:border-indigo-300 transition-colors"
              />
            </div>

            {/* ── AI Correction Section ──────────────────────────────────── */}
            {(() => {
              const correction = correctionsByPartKey[partKey];
              const hasEnoughWords = effectiveDraftWordCount >= 20;
              const alreadyCorrected = !!correction?.feedback;
              const isCorrectingNow = !!correction?.isCorrecting;
              const hasCorrectionError = correction?.error === 'failed';

              return (
                <div className={`${ELEVATED_SURFACE} p-4 sm:p-5`}>
                  <div className="flex items-center gap-2 mb-3">
                    <Sparkles className="w-4 h-4 text-purple-600 flex-shrink-0" />
                    <h3 className="font-black text-gray-900 text-base sm:text-lg">
                      {t('schreiben.aiCorrection', { defaultValue: "Logami's AI Correction" })}
                    </h3>
                  </div>

                  {/* Loading state */}
                  {isCorrectingNow && (
                    <div className="flex items-center gap-4 rounded-xl border border-purple-100 bg-purple-50 p-4 mb-4">
                      <div className="flex-shrink-0">
                        <LogamiLogoSpinner size="xl" decorative={false} alt="Logami loading" />
                      </div>
                      <div>
                        <p className="font-bold text-purple-800 text-sm">
                          {t('schreiben.correctionInProgress', { defaultValue: "Logami's AI is analysing your text…" })}
                        </p>
                        <p className="text-xs text-purple-600 mt-0.5">
                          {t('schreiben.correctionInProgressHint', { defaultValue: 'Evaluating vocabulary, grammar, coherence and style against exam criteria.' })}
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Error state */}
                  {hasCorrectionError && !isCorrectingNow && (
                    <div className="flex items-center gap-3 rounded-xl border border-red-200 bg-red-50 p-4 mb-4">
                      <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
                      <p className="text-sm text-red-700 font-semibold">
                        {t('schreiben.correctionFailed', { defaultValue: "Logami's AI correction failed. Please try again." })}
                      </p>
                      <button
                        onClick={submitForCorrection}
                        className="ml-auto min-h-[40px] inline-flex items-center gap-1.5 rounded-lg border border-red-300 px-3 py-1.5 text-xs font-semibold text-red-700 hover:bg-red-100 transition-all touch-manipulation"
                      >
                        <RotateCcw className="w-3.5 h-3.5" />
                        {t('schreiben.retry', { defaultValue: 'Retry' })}
                      </button>
                    </div>
                  )}

                  {/* Feedback display */}
                  {correction?.feedback && correction?.writingData && (
                    <div className="mb-4">
                      <PracticeWritingFeedback
                        feedback={correction.feedback}
                        writingData={correction.writingData}
                      />
                      <AskTeacherPanel submissionId={correction.submissionId} />
                    </div>
                  )}

                  {/* Submit button, available to all users */}
                  {!alreadyCorrected && !isCorrectingNow && (
                    <button
                      onClick={submitForCorrection}
                      disabled={!hasEnoughWords}
                      className={`w-full min-h-[52px] inline-flex items-center justify-center gap-2 rounded-xl font-bold text-sm touch-manipulation transition-all duration-300
                        ${hasEnoughWords
                          ? 'bg-gradient-to-r from-purple-700 via-purple-600 to-indigo-600 text-white shadow-[0_8px_22px_rgba(124,58,237,0.32)] hover:shadow-[0_12px_28px_rgba(124,58,237,0.44)] hover:-translate-y-0.5 active:scale-[0.98]'
                          : 'bg-gray-100 text-gray-400 cursor-not-allowed border border-gray-200'
                        }`}
                    >
                      <Send className="w-4 h-4" />
                      {hasEnoughWords
                        ? t('schreiben.submitForCorrection', { defaultValue: "Submit for Logami's AI Correction" })
                        : t('schreiben.writeLonger', { defaultValue: 'Write at least 20 words to submit' })}
                    </button>
                  )}

                  {/* Re-submit after correction */}
                  {alreadyCorrected && !isCorrectingNow && (
                    <button
                      onClick={submitForCorrection}
                      className="mt-3 min-h-[40px] inline-flex items-center gap-1.5 rounded-lg border border-purple-200 bg-white px-3 py-1.5 text-xs font-semibold text-purple-700 hover:bg-purple-50 transition-all touch-manipulation"
                    >
                      <RotateCcw className="w-3.5 h-3.5" />
                      {t('schreiben.resubmit', { defaultValue: "Re-submit for Logami's AI correction" })}
                    </button>
                  )}
                </div>
              );
            })()}
          </>
        )}
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white pb-20 sm:pb-10 animate-fade-in">
      <div className="max-w-4xl mx-auto px-4 py-5 sm:py-8">{content}</div>

      <GrammarBottomNav
        buttons={[
          {
            id: 'back',
            icon: ArrowLeft,
            label: t('grammar.back', { defaultValue: 'Back' }),
            onClick: handleBack,
            color: '#3A86FF',
          },
          {
            id: 'writing',
            icon: PenLine,
            label: t('schreiben.title'),
            onClick: () => { if (isFreeModeLockedToFirstProbe) { setPendingExitNav(() => () => navigate('/practice/writing')); setShowPremiumModal(true); trackPaywallShown({ context: 'schreiben' }); } else navigate('/practice/writing'); },
            active: true,
            color: '#3A86FF',
          },
          {
            id: 'home',
            icon: Home,
            label: t('nav.home', { defaultValue: 'Home' }),
            onClick: () => { if (isFreeModeLockedToFirstProbe) { setPendingExitNav(() => () => navigate('/dashboard')); setShowPremiumModal(true); trackPaywallShown({ context: 'schreiben' }); } else navigate('/dashboard'); },
            color: '#3A86FF',
          },
        ].filter(Boolean)}
      />

      <AhaUpsellModal
        open={showPremiumModal}
        onDismiss={handleAhaDismiss}
        onUpgrade={handleAhaUpgrade}
        context="schreiben"
      />
    </div>
  );
}
